import type { Language, Locale } from '@/config/locales';

export interface PaymentModalTranslation {
  payment: string;
  cardPayment: string;
  tableNumber: string;
  orderNumber: string;
  date: string;
  time: string;
  menu: string;
  qty: string;
  price: string;
  selectTip: string;
  customInput: string;
  percentage: string;
  amount: string;
  enterTip: string;
  enterTipPercentage: string;
  enterTipAmount: string;
  estimatedTip: string;
  cancel: string;
  points: string;
  available: string;
  pointsSection: string;
  pointsAvailable: string;
  pointsUsable: string;
  pointsToUse: string;
  pointsDiscount: string;
  pointsDiscountApplied: string;
  pointsMinAmount: string;
  loginToSeePoints: string;
  pointsLoadError: string;
  estimatedPoints: string;
  estimating: string;
  menuFirst: string;
  use: string;
  useAll: string;
  alwaysUseAll: string;
  giftCard: string;
  enterCode: string;
  apply: string;
  availablePoints: string;
  registerNewCard: string;
  subtotal: string;
  netPrice: string;
  tax: string;
  tip: string;
  total: string;
  orderSummary: string;
  makePayment: string;
  processing: string;
  processingMessage: string;
  paymentSuccess: string;
  paymentFailed: string;
  paymentFailedMessage: string;
  closeAndContinue: string;
  tryAgain: string;
  selectCardError: string;
  paymentMethod: string;
  storeNotFound: string;
  noOrderableItems: string;
  mixedStoreError: string;
  // #521 — Bloomin 카드 입력 단계 i18n (silent en fallback 차단)
  goBack: string;
  cardDetailsTitle: string;
  cardDetailsHint: string;
  amountToPay: string;
}

export interface BloominPaymentTranslation {
  goBack: string;
  title: string;
  description: string;
  amountToPay: string;
  invalidChallengeUrl: string;
  nonHttpsChallengeUrl: string;
  cardInput: {
    cardNumberLabel: string;
    cardNumberPlaceholder: string;
    expiryMonthLabel: string;
    expiryYearLabel: string;
    cvvLabel: string;
    cvvPlaceholder: string;
    holderNameLabel: string;
    holderNamePlaceholder: string;
    submitButton: string;
    securityNotice: string;
    errors: {
      cardNumberInvalid: string;
      expiryInvalid: string;
      cvvInvalid: string;
      holderNameRequired: string;
    };
  };
}

/**
 * 후불 "지금 결제" 전용 i18n (C3 #590, T4).
 *
 * 주문 내역의 후불 미결제 주문에서 카드 셀프 결제를 진입하는 버튼/모달 전용 문구.
 * 기존 Bloomin 카드 폼 라벨(cardInput)을 재사용하던 T2/T3 임시 라벨을 본 전용 키로 교체한다.
 *
 * ## 에러 라벨 의미 정합 (핵심)
 *   - `errors.notSettleable`: 주문 자체가 정산 불가 상태 — "카드번호 확인" 이 아님.
 *   - `errors.processingCannotCancel`: 결제 처리 중 — 잠시 후 재시도 안내.
 *   기존 임시 라벨은 위 두 코드를 카드번호 오류 문구로 매핑해 사용자에게 오해를 줬다.
 */
/**
 * 정산-시점 보정치(팁/포인트/쿠폰) 입력 + 미리보기 UI 라벨 (SP2-2b).
 *
 * 후불 "지금 결제" 모달에서 식후 정산 시 추가하는 팁(+)/포인트(−)/쿠폰(−) 입력과
 * 실시간 청구액 미리보기, 0원(전액 커버) 확정 버튼의 i18n 키 그룹.
 *
 * 기존 checkout 팁/포인트 어휘(paymentTranslations)와 어휘 일관성을 유지한다.
 */
export interface PayNowAdjustmentsTranslation {
  /** 섹션 헤더 — "정산 / 추가 결제" */
  sectionTitle: string;
  tip: {
    /** 팁 선택 라벨 */
    label: string;
    /** 직접 입력 버튼 라벨 */
    customLabel: string;
    /** 적용된 팁 금액 라벨 */
    amountLabel: string;
    /** CustomTipModal 라벨 (TipSelector.modalT) */
    enterTip: string;
    percentage: string;
    amount: string;
    enterTipPercentage: string;
    enterTipAmount: string;
    estimatedTip: string;
    cancel: string;
    apply: string;
  };
  points: {
    sectionTitle: string;
    available: string;
    usable: string;
    placeholder: string;
    apply: string;
    useAll: string;
    discountApplied: string;
    minAmount: string;
    /** 비회원 로그인 안내 (포인트 사용 불가) */
    loginPrompt: string;
  };
  coupon: {
    title: string;
    none: string;
    minOrderNotMet: string;
    expired: string;
    notUsable: string;
  };
  preview: {
    /** 미리보기 헤더 */
    title: string;
    /** 주문 금액 행 */
    orderTotal: string;
    /** 팁 행 */
    tip: string;
    /** 포인트 행 */
    points: string;
    /** 쿠폰 행 */
    coupon: string;
    /** 최종 청구액 행 */
    chargeAmount: string;
  };
  /** 청구액 0원 — 카드 없이 전액 결제 확정 버튼 */
  zeroChargeConfirm: string;
}

/**
 * 세션 전체 결제 UI 라벨 (#1705 R8).
 *
 * 주문 내역 진입 시 테이블 세션의 미결제 후불 N건을 1회 합산 결제하는 배너/버튼/모달 문구.
 * 미결제 1건이면 기존 단건 "지금 결제"(PayNowTranslation.button)를 유지하므로,
 * 본 그룹은 N≥2(또는 세션 합산 UI 진입) 시 노출되는 라벨이다.
 *
 * `{count}` / `{amount}` 는 호출자가 치환하는 플레이스홀더.
 */
export interface PayNowSessionTranslation {
  /** 세션 요약 배너 본문 — 예: "이 세션 미결제 {count}건 · {amount}" */
  bannerSummary: string;
  /** "전체 결제" CTA 버튼 */
  payAllButton: string;
  /** 세션 결제 모달 제목 */
  modalTitle: string;
  /** 세션 결제 모달 설명 — 예: "미결제 {count}건을 한 번에 결제합니다." */
  modalDescription: string;
  /**
   * 세션 토큰 부재 복구 안내 (#603 [4] Codex Critical) — 미결제 후불은 있으나
   * 세션 토큰(QR 스캔)이 유실되어 세션 결제 진입 불가일 때 노출. 단건결제 부활 대신
   * 토큰 복구(테이블 QR 재스캔)를 유도해 결제 도달성을 회복한다.
   */
  recoveryNotice: string;
  /**
   * [2] 진행 현황 바 본문 (#603) — Open 주문서의 진행 중 라인 수 {count}.
   * append 모델: {count} = items.length(라인 수, 미결제 건수 아님 — §0(E)).
   * 예: "진행 중 {count}건"
   */
  progressLabel: string;
  /**
   * [2] 진행 현황 바 결제 CTA (#603) — 장바구니가 비어 진행바가 확장됐을 때.
   * `payAllButton`(배너) 과 구분되는 메뉴 화면 전용 라벨. 예: "결제하기"
   */
  payButton: string;
  /** [3] 세션 주문 시트 제목 (#603) — 예: "우리 테이블 주문" */
  sheetTitle: string;
  /** [3] 세션 주문 시트 합계 라벨 (#603) — 예: "합계" */
  sheetTotalLabel: string;
  /** [3] 세션 주문 시트 전체 결제 CTA (#603) — 예: "전체 결제하기" */
  sheetPayButton: string;
  /** [3] 세션 주문 시트 빈 라인 안내 (#603) — 라인 0건(로딩/계약 위반) 시. 예: "표시할 주문이 없어요." */
  sheetEmpty: string;
  /** [3] 세션 주문 시트 닫기 버튼/오버레이 a11y 라벨 (#603 Codex Minor) — 예: "닫기" */
  sheetClose: string;
  /** #661 세션 결제 전용 페이지 — 뒤로가기 버튼 a11y 라벨. 예: "뒤로" */
  chargeBack: string;
  /** #661 세션 결제 전용 페이지 — 주문별 소계 라벨 (서버 권위 order.total). 예: "소계" */
  orderSubtotal: string;
}

export interface PayNowTranslation {
  /** 주문 카드의 "지금 결제" 진입 버튼 */
  button: string;
  /** 결제 모달 제목 */
  modalTitle: string;
  /** 모달 설명 (주문 번호는 별도 표기) */
  modalDescription: string;
  /** 결제 처리 중 안내 */
  processing: string;
  /** 세션 전체 결제 UI 라벨 (#1705 R8) */
  session: PayNowSessionTranslation;
  /** 정산-시점 보정치 입력 UI 라벨 (SP2-2b) */
  adjustments: PayNowAdjustmentsTranslation;
  errors: {
    /** 409 PAYMENT_NOT_SETTLEABLE — 주문을 지금 결제할 수 없음 (카드 오류 아님) */
    notSettleable: string;
    /** 409 PAYMENT_PROCESSING_CANNOT_CANCEL — 결제 처리 중, 잠시 후 재시도 */
    processingCannotCancel: string;
    /** challenge URL 신뢰 검증 실패 — 결제 인증 페이지를 열 수 없음 */
    untrustedChallenge: string;
    /**
     * 422 ADJUSTMENT_VALIDATION_FAILED — 보정치(포인트/쿠폰) 검증 실패 (SP2-2b).
     * 포인트 부족 / 쿠폰 무효 / 음수 청구 / 게스트 포인트 불가를 admin 이 단일 코드로 통합.
     */
    adjustmentValidationFailed: string;
    /** 422 CARD_REQUIRED_FOR_CHARGE — 청구액>0 인데 카드 미입력 (방어, SP2-2b) */
    cardRequiredForCharge: string;
    /** 그 외 일반 결제 실패 */
    generic: string;
  };
}

// #512 — Locale 단일 출처 정합.
export type PaymentModalTranslations =
  & Record<Language, PaymentModalTranslation>
  & Record<Exclude<Locale, Language>, PaymentModalTranslation>;

// #522 — Locale 단일 출처 정합 (15언어 전수).
export type BloominPaymentTranslations =
  & Record<Language, BloominPaymentTranslation>
  & Record<Exclude<Locale, Language>, BloominPaymentTranslation>;

// #512 — Locale 단일 출처 정합 (15언어 전수).
export type PayNowTranslations =
  & Record<Language, PayNowTranslation>
  & Record<Exclude<Locale, Language>, PayNowTranslation>;

export const bloominPaymentTranslations: BloominPaymentTranslations = {
  ko: {
    goBack: '돌아가기',
    title: '카드 정보 입력',
    description: '카드 정보를 입력한 뒤 3D Secure 인증으로 이동합니다.',
    amountToPay: '결제 금액',
    invalidChallengeUrl: '3D Secure 인증 URL이 올바르지 않습니다.',
    nonHttpsChallengeUrl: '3D Secure 인증 URL은 HTTPS여야 합니다.',
    cardInput: {
      cardNumberLabel: '카드 번호',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: '월',
      expiryYearLabel: '연도',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: '카드 소유자',
      holderNamePlaceholder: '카드에 표시된 이름',
      submitButton: '3D Secure 인증으로 이동',
      securityNotice: '카드 정보는 암호화된 연결로 결제사에 전달됩니다.',
      errors: {
        cardNumberInvalid: '카드 번호를 확인해 주세요.',
        expiryInvalid: '만료일을 확인해 주세요.',
        cvvInvalid: 'CVV를 확인해 주세요.',
        holderNameRequired: '카드 소유자 이름을 입력해 주세요.',
      },
    },
  },
  es: {
    goBack: 'Volver',
    title: 'Ingrese datos de tarjeta',
    description: 'Ingrese los datos de su tarjeta para continuar con 3D Secure.',
    amountToPay: 'Monto a pagar',
    invalidChallengeUrl: 'La URL de 3D Secure no es válida.',
    nonHttpsChallengeUrl: 'La URL de 3D Secure debe usar HTTPS.',
    cardInput: {
      cardNumberLabel: 'Número de tarjeta',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'Mes',
      expiryYearLabel: 'Año',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'Nombre del titular',
      holderNamePlaceholder: 'Como aparece en la tarjeta',
      submitButton: 'Continuar a 3D Secure',
      securityNotice: 'Los datos se envían al procesador mediante una conexión cifrada.',
      errors: {
        cardNumberInvalid: 'Verifica el número de tarjeta.',
        expiryInvalid: 'Verifica la fecha de vencimiento.',
        cvvInvalid: 'Verifica el CVV.',
        holderNameRequired: 'Ingresa el nombre del titular.',
      },
    },
  },
  en: {
    goBack: 'Go back',
    title: 'Enter card details',
    description: 'Enter your card details to continue with 3D Secure.',
    amountToPay: 'Amount to pay',
    invalidChallengeUrl: 'The 3D Secure URL is not valid.',
    nonHttpsChallengeUrl: 'The 3D Secure URL must use HTTPS.',
    cardInput: {
      cardNumberLabel: 'Card number',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'Month',
      expiryYearLabel: 'Year',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'Cardholder name',
      holderNamePlaceholder: 'As shown on card',
      submitButton: 'Continue to 3D Secure',
      securityNotice: 'Card data is sent to the processor over an encrypted connection.',
      errors: {
        cardNumberInvalid: 'Check the card number.',
        expiryInvalid: 'Check the expiry date.',
        cvvInvalid: 'Check the CVV.',
        holderNameRequired: 'Enter the cardholder name.',
      },
    },
  },
  pt: {
    goBack: 'Voltar',
    title: 'Insira os dados do cartão',
    description: 'Insira os dados do seu cartão para continuar com o 3D Secure.',
    amountToPay: 'Valor a pagar',
    invalidChallengeUrl: 'A URL do 3D Secure não é válida.',
    nonHttpsChallengeUrl: 'A URL do 3D Secure deve usar HTTPS.',
    cardInput: {
      cardNumberLabel: 'Número do cartão',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'Mês',
      expiryYearLabel: 'Ano',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'Nome do titular',
      holderNamePlaceholder: 'Como aparece no cartão',
      submitButton: 'Continuar para 3D Secure',
      securityNotice: 'Os dados do cartão são enviados ao processador por uma conexão criptografada.',
      errors: {
        cardNumberInvalid: 'Verifique o número do cartão.',
        expiryInvalid: 'Verifique a data de validade.',
        cvvInvalid: 'Verifique o CVV.',
        holderNameRequired: 'Informe o nome do titular.',
      },
    },
  },
  fr: {
    goBack: 'Retour',
    title: 'Saisissez les informations de carte',
    description: 'Saisissez les informations de votre carte pour continuer avec 3D Secure.',
    amountToPay: 'Montant à payer',
    invalidChallengeUrl: "L'URL 3D Secure n'est pas valide.",
    nonHttpsChallengeUrl: "L'URL 3D Secure doit utiliser HTTPS.",
    cardInput: {
      cardNumberLabel: 'Numéro de carte',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'Mois',
      expiryYearLabel: 'Année',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'Nom du titulaire',
      holderNamePlaceholder: 'Tel qu’indiqué sur la carte',
      submitButton: 'Continuer vers 3D Secure',
      securityNotice: 'Les données de carte sont transmises au processeur via une connexion chiffrée.',
      errors: {
        cardNumberInvalid: 'Vérifiez le numéro de carte.',
        expiryInvalid: "Vérifiez la date d'expiration.",
        cvvInvalid: 'Vérifiez le CVV.',
        holderNameRequired: 'Saisissez le nom du titulaire.',
      },
    },
  },
  zh: {
    goBack: '返回',
    title: '输入卡片信息',
    description: '输入您的卡片信息后将进入 3D Secure 验证。',
    amountToPay: '支付金额',
    invalidChallengeUrl: '3D Secure 验证 URL 无效。',
    nonHttpsChallengeUrl: '3D Secure 验证 URL 必须使用 HTTPS。',
    cardInput: {
      cardNumberLabel: '卡号',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: '月',
      expiryYearLabel: '年',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: '持卡人姓名',
      holderNamePlaceholder: '与卡片上显示一致',
      submitButton: '继续 3D Secure 验证',
      securityNotice: '卡片信息将通过加密连接发送给支付处理方。',
      errors: {
        cardNumberInvalid: '请检查卡号。',
        expiryInvalid: '请检查有效期。',
        cvvInvalid: '请检查 CVV。',
        holderNameRequired: '请输入持卡人姓名。',
      },
    },
  },
  ja: {
    goBack: '戻る',
    title: 'カード情報を入力',
    description: 'カード情報を入力すると 3D Secure 認証に進みます。',
    amountToPay: 'お支払い金額',
    invalidChallengeUrl: '3D Secure 認証 URL が正しくありません。',
    nonHttpsChallengeUrl: '3D Secure 認証 URL は HTTPS である必要があります。',
    cardInput: {
      cardNumberLabel: 'カード番号',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: '月',
      expiryYearLabel: '年',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'カード名義人',
      holderNamePlaceholder: 'カードに記載のとおり',
      submitButton: '3D Secure 認証へ進む',
      securityNotice: 'カード情報は暗号化された接続で決済処理会社に送信されます。',
      errors: {
        cardNumberInvalid: 'カード番号をご確認ください。',
        expiryInvalid: '有効期限をご確認ください。',
        cvvInvalid: 'CVV をご確認ください。',
        holderNameRequired: 'カード名義人の名前を入力してください。',
      },
    },
  },
  hi: {
    goBack: 'वापस जाएँ',
    title: 'कार्ड विवरण दर्ज करें',
    description: '3D Secure के साथ जारी रखने के लिए अपने कार्ड का विवरण दर्ज करें।',
    amountToPay: 'भुगतान राशि',
    invalidChallengeUrl: '3D Secure URL मान्य नहीं है।',
    nonHttpsChallengeUrl: '3D Secure URL में HTTPS होना आवश्यक है।',
    cardInput: {
      cardNumberLabel: 'कार्ड नंबर',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'महीना',
      expiryYearLabel: 'वर्ष',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'कार्डधारक का नाम',
      holderNamePlaceholder: 'जैसा कार्ड पर लिखा है',
      submitButton: '3D Secure पर जारी रखें',
      securityNotice: 'कार्ड डेटा एन्क्रिप्टेड कनेक्शन के माध्यम से प्रोसेसर को भेजा जाता है।',
      errors: {
        cardNumberInvalid: 'कार्ड नंबर जाँचें।',
        expiryInvalid: 'समाप्ति तिथि जाँचें।',
        cvvInvalid: 'CVV जाँचें।',
        holderNameRequired: 'कार्डधारक का नाम दर्ज करें।',
      },
    },
  },
  de: {
    goBack: 'Zurück',
    title: 'Kartendaten eingeben',
    description: 'Geben Sie Ihre Kartendaten ein, um mit 3D Secure fortzufahren.',
    amountToPay: 'Zu zahlender Betrag',
    invalidChallengeUrl: 'Die 3D-Secure-URL ist ungültig.',
    nonHttpsChallengeUrl: 'Die 3D-Secure-URL muss HTTPS verwenden.',
    cardInput: {
      cardNumberLabel: 'Kartennummer',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'Monat',
      expiryYearLabel: 'Jahr',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'Karteninhaber',
      holderNamePlaceholder: 'Wie auf der Karte angegeben',
      submitButton: 'Weiter zu 3D Secure',
      securityNotice: 'Die Kartendaten werden über eine verschlüsselte Verbindung an den Zahlungsdienstleister übertragen.',
      errors: {
        cardNumberInvalid: 'Bitte überprüfen Sie die Kartennummer.',
        expiryInvalid: 'Bitte überprüfen Sie das Ablaufdatum.',
        cvvInvalid: 'Bitte überprüfen Sie den CVV.',
        holderNameRequired: 'Bitte geben Sie den Namen des Karteninhabers ein.',
      },
    },
  },
  it: {
    goBack: 'Indietro',
    title: 'Inserisci i dati della carta',
    description: 'Inserisci i dati della tua carta per continuare con 3D Secure.',
    amountToPay: 'Importo da pagare',
    invalidChallengeUrl: "L'URL 3D Secure non è valido.",
    nonHttpsChallengeUrl: "L'URL 3D Secure deve usare HTTPS.",
    cardInput: {
      cardNumberLabel: 'Numero di carta',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'Mese',
      expiryYearLabel: 'Anno',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'Intestatario della carta',
      holderNamePlaceholder: 'Come indicato sulla carta',
      submitButton: 'Continua con 3D Secure',
      securityNotice: 'I dati della carta vengono inviati al processore tramite una connessione crittografata.',
      errors: {
        cardNumberInvalid: 'Controlla il numero della carta.',
        expiryInvalid: 'Controlla la data di scadenza.',
        cvvInvalid: 'Controlla il CVV.',
        holderNameRequired: "Inserisci il nome dell'intestatario.",
      },
    },
  },
  ru: {
    goBack: 'Назад',
    title: 'Введите данные карты',
    description: 'Введите данные вашей карты, чтобы продолжить с 3D Secure.',
    amountToPay: 'Сумма к оплате',
    invalidChallengeUrl: 'URL 3D Secure недействителен.',
    nonHttpsChallengeUrl: 'URL 3D Secure должен использовать HTTPS.',
    cardInput: {
      cardNumberLabel: 'Номер карты',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'Месяц',
      expiryYearLabel: 'Год',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'Имя владельца карты',
      holderNamePlaceholder: 'Как указано на карте',
      submitButton: 'Перейти к 3D Secure',
      securityNotice: 'Данные карты передаются процессинговому центру по зашифрованному соединению.',
      errors: {
        cardNumberInvalid: 'Проверьте номер карты.',
        expiryInvalid: 'Проверьте срок действия.',
        cvvInvalid: 'Проверьте CVV.',
        holderNameRequired: 'Введите имя владельца карты.',
      },
    },
  },
  ar: {
    goBack: 'رجوع',
    title: 'أدخل بيانات البطاقة',
    description: 'أدخل بيانات بطاقتك للمتابعة مع 3D Secure.',
    amountToPay: 'المبلغ المستحق',
    invalidChallengeUrl: 'رابط 3D Secure غير صالح.',
    nonHttpsChallengeUrl: 'يجب أن يستخدم رابط 3D Secure بروتوكول HTTPS.',
    cardInput: {
      cardNumberLabel: 'رقم البطاقة',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'الشهر',
      expiryYearLabel: 'السنة',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'اسم حامل البطاقة',
      holderNamePlaceholder: 'كما هو مدوّن على البطاقة',
      submitButton: 'المتابعة إلى 3D Secure',
      securityNotice: 'تُرسل بيانات البطاقة إلى معالج الدفع عبر اتصال مشفّر.',
      errors: {
        cardNumberInvalid: 'يرجى التحقق من رقم البطاقة.',
        expiryInvalid: 'يرجى التحقق من تاريخ الانتهاء.',
        cvvInvalid: 'يرجى التحقق من CVV.',
        holderNameRequired: 'يرجى إدخال اسم حامل البطاقة.',
      },
    },
  },
  vi: {
    goBack: 'Quay lại',
    title: 'Nhập thông tin thẻ',
    description: 'Nhập thông tin thẻ của bạn để tiếp tục với 3D Secure.',
    amountToPay: 'Số tiền thanh toán',
    invalidChallengeUrl: 'URL xác thực 3D Secure không hợp lệ.',
    nonHttpsChallengeUrl: 'URL xác thực 3D Secure phải sử dụng HTTPS.',
    cardInput: {
      cardNumberLabel: 'Số thẻ',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'Tháng',
      expiryYearLabel: 'Năm',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'Tên chủ thẻ',
      holderNamePlaceholder: 'Như in trên thẻ',
      submitButton: 'Tiếp tục đến 3D Secure',
      securityNotice: 'Thông tin thẻ được gửi đến đơn vị xử lý thanh toán qua kết nối mã hóa.',
      errors: {
        cardNumberInvalid: 'Vui lòng kiểm tra số thẻ.',
        expiryInvalid: 'Vui lòng kiểm tra ngày hết hạn.',
        cvvInvalid: 'Vui lòng kiểm tra CVV.',
        holderNameRequired: 'Vui lòng nhập tên chủ thẻ.',
      },
    },
  },
  th: {
    goBack: 'ย้อนกลับ',
    title: 'กรอกข้อมูลบัตร',
    description: 'กรอกข้อมูลบัตรของคุณเพื่อดำเนินการต่อด้วย 3D Secure',
    amountToPay: 'ยอดที่ต้องชำระ',
    invalidChallengeUrl: 'URL ของ 3D Secure ไม่ถูกต้อง',
    nonHttpsChallengeUrl: 'URL ของ 3D Secure ต้องใช้ HTTPS',
    cardInput: {
      cardNumberLabel: 'หมายเลขบัตร',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'เดือน',
      expiryYearLabel: 'ปี',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'ชื่อผู้ถือบัตร',
      holderNamePlaceholder: 'ตามที่ปรากฏบนบัตร',
      submitButton: 'ดำเนินการต่อไปยัง 3D Secure',
      securityNotice: 'ข้อมูลบัตรจะถูกส่งไปยังผู้ประมวลผลการชำระเงินผ่านการเชื่อมต่อที่เข้ารหัส',
      errors: {
        cardNumberInvalid: 'กรุณาตรวจสอบหมายเลขบัตร',
        expiryInvalid: 'กรุณาตรวจสอบวันหมดอายุ',
        cvvInvalid: 'กรุณาตรวจสอบ CVV',
        holderNameRequired: 'กรุณากรอกชื่อผู้ถือบัตร',
      },
    },
  },
  nl: {
    goBack: 'Terug',
    title: 'Voer kaartgegevens in',
    description: 'Voer uw kaartgegevens in om door te gaan met 3D Secure.',
    amountToPay: 'Te betalen bedrag',
    invalidChallengeUrl: 'De 3D Secure-URL is ongeldig.',
    nonHttpsChallengeUrl: 'De 3D Secure-URL moet HTTPS gebruiken.',
    cardInput: {
      cardNumberLabel: 'Kaartnummer',
      cardNumberPlaceholder: '1234 1234 1234 1234',
      expiryMonthLabel: 'Maand',
      expiryYearLabel: 'Jaar',
      cvvLabel: 'CVV',
      cvvPlaceholder: '123',
      holderNameLabel: 'Naam kaarthouder',
      holderNamePlaceholder: 'Zoals vermeld op de kaart',
      submitButton: 'Doorgaan naar 3D Secure',
      securityNotice: 'Kaartgegevens worden via een versleutelde verbinding naar de betalingsverwerker verzonden.',
      errors: {
        cardNumberInvalid: 'Controleer het kaartnummer.',
        expiryInvalid: 'Controleer de vervaldatum.',
        cvvInvalid: 'Controleer de CVV.',
        holderNameRequired: 'Voer de naam van de kaarthouder in.',
      },
    },
  },
};

export const paymentTranslations: PaymentModalTranslations = {
  ko: {
    payment: '결제',
    cardPayment: '카드 결제',
    tableNumber: '테이블 번호',
    orderNumber: '주문 번호',
    date: '날짜',
    time: '시간',
    menu: '메뉴',
    qty: '수량',
    price: '가격',
    selectTip: '서버 팁 선택',
    customInput: '입력',
    percentage: '%',
    amount: '금액',
    enterTip: '팁 입력',
    enterTipPercentage: '팁 비율을 입력하세요',
    enterTipAmount: '팁 금액을 입력하세요',
    estimatedTip: '예상 팁',
    cancel: '취소',
    points: '포인트',
    available: '보유',
    pointsSection: '포인트 사용',
    pointsAvailable: '보유 포인트',
    pointsUsable: '사용 가능',
    pointsToUse: '사용할 포인트',
    pointsDiscount: '포인트 할인',
    pointsDiscountApplied: '할인 적용됨',
    pointsMinAmount: '최소 사용 금액:',
    loginToSeePoints: '포인트를 사용하려면 로그인하세요',
    pointsLoadError: '포인트 정보를 불러오지 못했습니다',
    estimatedPoints: '적립 예정 포인트',
    estimating: '계산 중...',
    menuFirst: '메뉴 우선사용',
    use: '사용',
    useAll: '전액사용',
    alwaysUseAll: '항상 전액사용',
    giftCard: '기프트 카드',
    enterCode: '코드를 입력해주세요',
    apply: '적용',
    availablePoints: '사용 가능한 포인트가 있어요',
    registerNewCard: '새 카드 등록',
    subtotal: '세금 포함 소계',
    netPrice: '순가격',
    tax: '세금',
    tip: '팁',
    total: '총합',
    orderSummary: '주문 요약',
    makePayment: '결제하기',
    processing: '결제 처리 중...',
    processingMessage: '결제를 처리하는 동안 잠시 기다려 주세요...',
    paymentSuccess: '결제 완료!',
    paymentFailed: '결제 실패',
    paymentFailedMessage: '결제에 문제가 발생했습니다. 다시 시도해 주세요.',
    closeAndContinue: '닫기',
    tryAgain: '다시 시도',
    selectCardError: '결제 카드를 선택해 주세요',
    paymentMethod: '결제 수단',
    storeNotFound: '매장 정보를 찾을 수 없습니다.',
    noOrderableItems: '주문 가능한 메뉴가 없습니다.',
    mixedStoreError: '장바구니에 다른 매장의 메뉴가 섞여있습니다.',
    goBack: '돌아가기',
    cardDetailsTitle: '카드 정보 입력',
    cardDetailsHint: '아래에 카드 정보를 입력하여 결제를 완료하세요.',
    amountToPay: '결제 금액',
  },
  es: {
    payment: 'Pago',
    cardPayment: 'Pago con Tarjeta',
    tableNumber: 'Número de Mesa',
    orderNumber: 'Número de Pedido',
    date: 'Fecha',
    time: 'Hora',
    menu: 'Menú',
    qty: 'Cant.',
    price: 'Precio',
    selectTip: 'Seleccionar propina para el servidor',
    customInput: 'OTRO',
    percentage: '%',
    amount: 'Cantidad',
    enterTip: 'Ingresar propina',
    enterTipPercentage: 'Ingresa porcentaje de propina',
    enterTipAmount: 'Ingresa cantidad de propina',
    estimatedTip: 'Propina estimada',
    cancel: 'Cancelar',
    points: 'Puntos',
    available: 'Disponible',
    pointsSection: 'Usar puntos',
    pointsAvailable: 'Puntos disponibles',
    pointsUsable: 'Disponible',
    pointsToUse: 'Puntos a usar',
    pointsDiscount: 'Descuento de puntos',
    pointsDiscountApplied: 'descuento aplicado',
    pointsMinAmount: 'Monto mínimo:',
    loginToSeePoints: 'Inicia sesión para usar puntos',
    pointsLoadError: 'No se pudo cargar la información de puntos',
    estimatedPoints: 'Puntos a acumular',
    estimating: 'Calculando...',
    menuFirst: 'Usar menú primero',
    use: 'Usar',
    useAll: 'Usar todo',
    alwaysUseAll: 'Siempre usar todo',
    giftCard: 'Tarjeta de regalo',
    enterCode: 'Ingresa el código',
    apply: 'Aplicar',
    availablePoints: 'Tienes puntos disponibles',
    registerNewCard: 'Registrar nueva tarjeta',
    subtotal: 'Subtotal con impuesto',
    netPrice: 'Precio neto',
    tax: 'Impuesto',
    tip: 'Propina',
    total: 'Total',
    orderSummary: 'Resumen del pedido',
    makePayment: 'Realizar Pago',
    processing: 'Procesando pago...',
    processingMessage: 'Por favor espera mientras procesamos tu pago...',
    paymentSuccess: '¡Pago Exitoso!',
    paymentFailed: 'Pago Fallido',
    paymentFailedMessage: 'Algo salió mal con tu pago. Por favor intenta de nuevo.',
    closeAndContinue: 'Cerrar',
    tryAgain: 'Intentar de Nuevo',
    selectCardError: 'Por favor selecciona una tarjeta de pago',
    paymentMethod: 'Método de Pago',
    storeNotFound: 'No se encontró la información de la tienda.',
    noOrderableItems: 'No hay artículos disponibles para ordenar.',
    mixedStoreError: 'El carrito contiene artículos de diferentes tiendas.',
    goBack: 'Volver',
    cardDetailsTitle: 'Ingrese datos de tarjeta',
    cardDetailsHint: 'Ingrese los datos de su tarjeta para completar el pago.',
    amountToPay: 'Monto a pagar',
  },
  en: {
    payment: 'Payment',
    cardPayment: 'Card Payment',
    tableNumber: 'Table Number',
    orderNumber: 'Order Number',
    date: 'Date',
    time: 'Time',
    menu: 'Menu',
    qty: 'Qty',
    price: 'Price',
    selectTip: 'Select tip for server',
    customInput: 'OTHER',
    percentage: '%',
    amount: 'Amount',
    enterTip: 'Enter tip',
    enterTipPercentage: 'Enter tip percentage',
    enterTipAmount: 'Enter tip amount',
    estimatedTip: 'Estimated tip',
    cancel: 'Cancel',
    points: 'Points',
    available: 'Available',
    pointsSection: 'Use points',
    pointsAvailable: 'Available points',
    pointsUsable: 'Usable',
    pointsToUse: 'Points to use',
    pointsDiscount: 'Points discount',
    pointsDiscountApplied: 'discount applied',
    pointsMinAmount: 'Minimum amount:',
    loginToSeePoints: 'Log in to use points',
    pointsLoadError: 'Failed to load points info',
    estimatedPoints: 'Points to earn',
    estimating: 'Calculating...',
    menuFirst: 'Menu first use',
    use: 'Use',
    useAll: 'Use all',
    alwaysUseAll: 'Always use all',
    giftCard: 'Gift Card',
    enterCode: 'Enter code',
    apply: 'Apply',
    availablePoints: 'You have available points',
    registerNewCard: 'Register a new card',
    subtotal: 'Subtotal with tax',
    netPrice: 'Net price',
    tax: 'Tax',
    tip: 'Tip',
    total: 'Total',
    orderSummary: 'Order Summary',
    makePayment: 'Make Payment',
    processing: 'Processing payment...',
    processingMessage: 'Please wait while we process your payment...',
    paymentSuccess: 'Payment Successful!',
    paymentFailed: 'Payment Failed',
    paymentFailedMessage: 'Something went wrong with your payment. Please try again.',
    closeAndContinue: 'Close',
    tryAgain: 'Try Again',
    selectCardError: 'Please select a payment card',
    paymentMethod: 'Payment Method',
    storeNotFound: 'Store information not found.',
    noOrderableItems: 'No orderable items available.',
    mixedStoreError: 'Cart contains items from different stores.',
    goBack: 'Go back',
    cardDetailsTitle: 'Enter card details',
    cardDetailsHint: 'Enter your card details below to complete the payment.',
    amountToPay: 'Amount to pay',
  },
  pt: {
    payment: 'Pagamento',
    cardPayment: 'Pagamento com cartão',
    tableNumber: 'Número da mesa',
    orderNumber: 'Número do pedido',
    date: 'Data',
    time: 'Hora',
    menu: 'Menu',
    qty: 'Qtd.',
    price: 'Preço',
    selectTip: 'Selecionar gorjeta para o garçom',
    customInput: 'OUTRO',
    percentage: '%',
    amount: 'Valor',
    enterTip: 'Inserir gorjeta',
    enterTipPercentage: 'Insira a porcentagem da gorjeta',
    enterTipAmount: 'Insira o valor da gorjeta',
    estimatedTip: 'Gorjeta estimada',
    cancel: 'Cancelar',
    points: 'Pontos',
    available: 'Disponível',
    pointsSection: 'Usar pontos',
    pointsAvailable: 'Pontos disponíveis',
    pointsUsable: 'Disponível',
    pointsToUse: 'Pontos a usar',
    pointsDiscount: 'Desconto de pontos',
    pointsDiscountApplied: 'desconto aplicado',
    pointsMinAmount: 'Valor mínimo:',
    loginToSeePoints: 'Faça login para usar pontos',
    pointsLoadError: 'Falha ao carregar informações de pontos',
    estimatedPoints: 'Pontos a ganhar',
    estimating: 'Calculando...',
    menuFirst: 'Usar menu primeiro',
    use: 'Usar',
    useAll: 'Usar tudo',
    alwaysUseAll: 'Sempre usar tudo',
    giftCard: 'Cartão-presente',
    enterCode: 'Digite o código',
    apply: 'Aplicar',
    availablePoints: 'Você tem pontos disponíveis',
    registerNewCard: 'Registrar novo cartão',
    subtotal: 'Subtotal com imposto',
    netPrice: 'Preço líquido',
    tax: 'Imposto',
    tip: 'Gorjeta',
    total: 'Total',
    orderSummary: 'Resumo do pedido',
    makePayment: 'Fazer pagamento',
    processing: 'Processando pagamento...',
    processingMessage: 'Por favor, aguarde enquanto processamos seu pagamento...',
    paymentSuccess: 'Pagamento bem-sucedido!',
    paymentFailed: 'Pagamento falhou',
    paymentFailedMessage: 'Algo deu errado com seu pagamento. Por favor, tente novamente.',
    closeAndContinue: 'Fechar',
    tryAgain: 'Tentar novamente',
    selectCardError: 'Por favor, selecione um cartão de pagamento',
    paymentMethod: 'Método de pagamento',
    storeNotFound: 'Informações da loja não encontradas.',
    noOrderableItems: 'Não há itens disponíveis para pedir.',
    mixedStoreError: 'O carrinho contém itens de lojas diferentes.',
    goBack: 'Voltar',
    cardDetailsTitle: 'Inserir dados do cartão',
    cardDetailsHint: 'Insira os dados do seu cartão abaixo para concluir o pagamento.',
    amountToPay: 'Valor a pagar',
  },
  fr: {
    payment: 'Paiement',
    cardPayment: 'Paiement par carte',
    tableNumber: 'Numéro de table',
    orderNumber: 'Numéro de commande',
    date: 'Date',
    time: 'Heure',
    menu: 'Menu',
    qty: 'Qté',
    price: 'Prix',
    selectTip: 'Sélectionner un pourboire pour le serveur',
    customInput: 'AUTRE',
    percentage: '%',
    amount: 'Montant',
    enterTip: 'Saisir le pourboire',
    enterTipPercentage: 'Saisir le pourcentage du pourboire',
    enterTipAmount: 'Saisir le montant du pourboire',
    estimatedTip: 'Pourboire estimé',
    cancel: 'Annuler',
    points: 'Points',
    available: 'Disponible',
    pointsSection: 'Utiliser des points',
    pointsAvailable: 'Points disponibles',
    pointsUsable: 'Utilisable',
    pointsToUse: 'Points à utiliser',
    pointsDiscount: 'Réduction de points',
    pointsDiscountApplied: 'réduction appliquée',
    pointsMinAmount: 'Montant minimum:',
    loginToSeePoints: 'Connectez-vous pour utiliser les points',
    pointsLoadError: 'Impossible de charger les informations de points',
    estimatedPoints: 'Points à gagner',
    estimating: 'Calcul...',
    menuFirst: 'Utiliser le menu en premier',
    use: 'Utiliser',
    useAll: 'Tout utiliser',
    alwaysUseAll: 'Toujours tout utiliser',
    giftCard: 'Carte-cadeau',
    enterCode: 'Saisir le code',
    apply: 'Appliquer',
    availablePoints: 'Vous avez des points disponibles',
    registerNewCard: 'Enregistrer une nouvelle carte',
    subtotal: 'Sous-total avec taxe',
    netPrice: 'Prix net',
    tax: 'Taxe',
    tip: 'Pourboire',
    total: 'Total',
    orderSummary: 'Récapitulatif de la commande',
    makePayment: 'Effectuer le paiement',
    processing: 'Traitement du paiement...',
    processingMessage: 'Veuillez patienter pendant que nous traitons votre paiement...',
    paymentSuccess: 'Paiement réussi !',
    paymentFailed: 'Paiement échoué',
    paymentFailedMessage: 'Une erreur s’est produite avec votre paiement. Veuillez réessayer.',
    closeAndContinue: 'Fermer',
    tryAgain: 'Réessayer',
    selectCardError: 'Veuillez sélectionner une carte de paiement',
    paymentMethod: 'Mode de paiement',
    storeNotFound: 'Informations sur le magasin introuvables.',
    noOrderableItems: 'Aucun article disponible pour commander.',
    mixedStoreError: 'Le panier contient des articles de différents magasins.',
    goBack: 'Retour',
    cardDetailsTitle: 'Saisir les informations de la carte',
    cardDetailsHint: 'Saisissez les informations de votre carte ci-dessous pour finaliser le paiement.',
    amountToPay: 'Montant à payer',
  },
  zh: {
    payment: '支付',
    cardPayment: '卡支付',
    tableNumber: '桌号',
    orderNumber: '订单号',
    date: '日期',
    time: '时间',
    menu: '菜单',
    qty: '数量',
    price: '价格',
    selectTip: '选择服务员小费',
    customInput: '其他',
    percentage: '%',
    amount: '金额',
    enterTip: '输入小费',
    enterTipPercentage: '输入小费百分比',
    enterTipAmount: '输入小费金额',
    estimatedTip: '预计小费',
    cancel: '取消',
    points: '积分',
    available: '可用',
    pointsSection: '使用积分',
    pointsAvailable: '可用积分',
    pointsUsable: '可用',
    pointsToUse: '要使用的积分',
    pointsDiscount: '积分折扣',
    pointsDiscountApplied: '已应用折扣',
    pointsMinAmount: '最低金额:',
    loginToSeePoints: '登录以使用积分',
    pointsLoadError: '加载积分信息失败',
    estimatedPoints: '预计获得积分',
    estimating: '计算中...',
    menuFirst: '优先使用菜单',
    use: '使用',
    useAll: '全部使用',
    alwaysUseAll: '始终全部使用',
    giftCard: '礼品卡',
    enterCode: '输入代码',
    apply: '应用',
    availablePoints: '您有可用的积分',
    registerNewCard: '注册新卡',
    subtotal: '含税小计',
    netPrice: '净价',
    tax: '税',
    tip: '小费',
    total: '总计',
    orderSummary: '订单摘要',
    makePayment: '进行支付',
    processing: '处理支付中...',
    processingMessage: '请稍候，正在处理您的付款...',
    paymentSuccess: '支付成功!',
    paymentFailed: '支付失败',
    paymentFailedMessage: '您的付款出现问题。请重试。',
    closeAndContinue: '关闭',
    tryAgain: '重试',
    selectCardError: '请选择支付卡',
    paymentMethod: '支付方式',
    storeNotFound: '未找到门店信息。',
    noOrderableItems: '没有可订购的项目。',
    mixedStoreError: '购物车包含不同门店的项目。',
    goBack: '返回',
    cardDetailsTitle: '输入卡片信息',
    cardDetailsHint: '请在下方输入您的卡片信息以完成支付。',
    amountToPay: '应付金额',
  },
  ja: {
    payment: '支払い',
    cardPayment: 'カード支払い',
    tableNumber: 'テーブル番号',
    orderNumber: '注文番号',
    date: '日付',
    time: '時間',
    menu: 'メニュー',
    qty: '数量',
    price: '価格',
    selectTip: 'スタッフへのチップを選択',
    customInput: 'その他',
    percentage: '%',
    amount: '金額',
    enterTip: 'チップを入力',
    enterTipPercentage: 'チップの割合を入力',
    enterTipAmount: 'チップの金額を入力',
    estimatedTip: '推定チップ',
    cancel: 'キャンセル',
    points: 'ポイント',
    available: '利用可能',
    pointsSection: 'ポイントを使用',
    pointsAvailable: '利用可能ポイント',
    pointsUsable: '使用可能',
    pointsToUse: '使用するポイント',
    pointsDiscount: 'ポイント割引',
    pointsDiscountApplied: '割引適用済み',
    pointsMinAmount: '最低金額:',
    loginToSeePoints: 'ポイントを使用するにはログインしてください',
    pointsLoadError: 'ポイント情報の読み込みに失敗しました',
    estimatedPoints: '獲得予定ポイント',
    estimating: '計算中...',
    menuFirst: 'メニュー優先使用',
    use: '使用',
    useAll: '全額使用',
    alwaysUseAll: '常に全額使用',
    giftCard: 'ギフトカード',
    enterCode: 'コードを入力',
    apply: '適用',
    availablePoints: '利用可能なポイントがあります',
    registerNewCard: '新しいカードを登録',
    subtotal: '税込み小計',
    netPrice: '正味価格',
    tax: '税',
    tip: 'チップ',
    total: '合計',
    orderSummary: '注文概要',
    makePayment: '支払いを行う',
    processing: '支払い処理中...',
    processingMessage: '支払いを処理中です。しばらくお待ちください...',
    paymentSuccess: '支払い成功！',
    paymentFailed: '支払い失敗',
    paymentFailedMessage: '支払いに問題が発生しました。もう一度お試しください。',
    closeAndContinue: '閉じる',
    tryAgain: '再試行',
    selectCardError: '支払いカードを選択してください',
    paymentMethod: '支払い方法',
    storeNotFound: '店舗情報が見つかりません。',
    noOrderableItems: '注文可能な項目がありません。',
    mixedStoreError: 'カートに異なる店舗の項目が含まれています。',
    goBack: '戻る',
    cardDetailsTitle: 'カード情報を入力',
    cardDetailsHint: '以下にカード情報を入力して支払いを完了してください。',
    amountToPay: 'お支払い金額',
  },
  hi: {
    payment: 'भुगतान',
    cardPayment: 'कार्ड भुगतान',
    tableNumber: 'टेबल नंबर',
    orderNumber: 'ऑर्डर नंबर',
    date: 'दिनांक',
    time: 'समय',
    menu: 'मेनू',
    qty: 'मात्रा',
    price: 'कीमत',
    selectTip: 'सर्वर के लिए टिप चुनें',
    customInput: 'अन्य',
    percentage: '%',
    amount: 'राशि',
    enterTip: 'टिप दर्ज करें',
    enterTipPercentage: 'टिप प्रतिशत दर्ज करें',
    enterTipAmount: 'टिप राशि दर्ज करें',
    estimatedTip: 'अनुमानित टिप',
    cancel: 'रद्द करें',
    points: 'अंक',
    available: 'उपलब्ध',
    pointsSection: 'अंक उपयोग करें',
    pointsAvailable: 'उपलब्ध अंक',
    pointsUsable: 'उपयोग योग्य',
    pointsToUse: 'उपयोग करने के अंक',
    pointsDiscount: 'अंक छूट',
    pointsDiscountApplied: 'छूट लागू',
    pointsMinAmount: 'न्यूनतम राशि:',
    loginToSeePoints: 'अंक उपयोग करने के लिए लॉग इन करें',
    pointsLoadError: 'अंक जानकारी लोड करने में विफल',
    estimatedPoints: 'मिलने वाले अंक',
    estimating: 'गणना हो रही है...',
    menuFirst: 'मेनू पहले उपयोग करें',
    use: 'उपयोग करें',
    useAll: 'सब उपयोग करें',
    alwaysUseAll: 'हमेशा सब उपयोग करें',
    giftCard: 'गिफ्ट कार्ड',
    enterCode: 'कोड दर्ज करें',
    apply: 'लागू करें',
    availablePoints: 'आपके पास उपलब्ध अंक हैं',
    registerNewCard: 'नया कार्ड पंजीकृत करें',
    subtotal: 'कर सहित उप-योग',
    netPrice: 'शुद्ध मूल्य',
    tax: 'कर',
    tip: 'टिप',
    total: 'कुल',
    orderSummary: 'ऑर्डर सारांश',
    makePayment: 'भुगतान करें',
    processing: 'भुगतान संसाधित हो रहा है...',
    processingMessage: 'कृपया प्रतीक्षा करें जब हम आपके भुगतान को संसाधित कर रहे हैं...',
    paymentSuccess: 'भुगतान सफल!',
    paymentFailed: 'भुगतान विफल',
    paymentFailedMessage: 'आपके भुगतान में कुछ गलत हो गया। कृपया पुनः प्रयास करें।',
    closeAndContinue: 'बंद करें',
    tryAgain: 'पुनः प्रयास करें',
    selectCardError: 'कृपया भुगतान कार्ड चुनें',
    paymentMethod: 'भुगतान विधि',
    storeNotFound: 'स्टोर जानकारी नहीं मिली।',
    noOrderableItems: 'कोई ऑर्डर करने योग्य आइटम उपलब्ध नहीं है।',
    mixedStoreError: 'कार्ट में विभिन्न स्टोर्स के आइटम हैं।',
    goBack: 'वापस जाएं',
    cardDetailsTitle: 'कार्ड विवरण दर्ज करें',
    cardDetailsHint: 'भुगतान पूरा करने के लिए नीचे अपने कार्ड का विवरण दर्ज करें।',
    amountToPay: 'भुगतान राशि',
  },
  de: {
    payment: 'Zahlung',
    cardPayment: 'Kartenzahlung',
    tableNumber: 'Tischnummer',
    orderNumber: 'Bestellnummer',
    date: 'Datum',
    time: 'Uhrzeit',
    menu: 'Menü',
    qty: 'Anz.',
    price: 'Preis',
    selectTip: 'Trinkgeld für Server auswählen',
    customInput: 'ANDERE',
    percentage: '%',
    amount: 'Betrag',
    enterTip: 'Trinkgeld eingeben',
    enterTipPercentage: 'Trinkgeldprozentsatz eingeben',
    enterTipAmount: 'Trinkgeldbetrag eingeben',
    estimatedTip: 'Geschätztes Trinkgeld',
    cancel: 'Abbrechen',
    points: 'Punkte',
    available: 'Verfügbar',
    pointsSection: 'Punkte verwenden',
    pointsAvailable: 'Verfügbare Punkte',
    pointsUsable: 'Nutzbar',
    pointsToUse: 'Zu verwendende Punkte',
    pointsDiscount: 'Punkterabatt',
    pointsDiscountApplied: 'Rabatt angewendet',
    pointsMinAmount: 'Mindestbetrag:',
    loginToSeePoints: 'Melden Sie sich an, um Punkte zu verwenden',
    pointsLoadError: 'Punkteinformationen konnten nicht geladen werden',
    estimatedPoints: 'Zu sammelnde Punkte',
    estimating: 'Berechnung...',
    menuFirst: 'Menü zuerst verwenden',
    use: 'Verwenden',
    useAll: 'Alles verwenden',
    alwaysUseAll: 'Immer alles verwenden',
    giftCard: 'Geschenkkarte',
    enterCode: 'Code eingeben',
    apply: 'Anwenden',
    availablePoints: 'Sie haben verfügbare Punkte',
    registerNewCard: 'Neue Karte registrieren',
    subtotal: 'Zwischensumme mit Steuer',
    netPrice: 'Nettopreis',
    tax: 'Steuer',
    tip: 'Trinkgeld',
    total: 'Gesamt',
    orderSummary: 'Bestellübersicht',
    makePayment: 'Zahlung leisten',
    processing: 'Zahlung wird verarbeitet...',
    processingMessage: 'Bitte warten Sie, während wir Ihre Zahlung verarbeiten...',
    paymentSuccess: 'Zahlung erfolgreich!',
    paymentFailed: 'Zahlung fehlgeschlagen',
    paymentFailedMessage: 'Mit Ihrer Zahlung ist etwas schiefgelaufen. Bitte versuchen Sie es erneut.',
    closeAndContinue: 'Schließen',
    tryAgain: 'Erneut versuchen',
    selectCardError: 'Bitte wählen Sie eine Zahlungskarte',
    paymentMethod: 'Zahlungsmethode',
    storeNotFound: 'Geschäftsinformationen nicht gefunden.',
    noOrderableItems: 'Keine bestellbaren Artikel verfügbar.',
    mixedStoreError: 'Der Warenkorb enthält Artikel aus verschiedenen Geschäften.',
    goBack: 'Zurück',
    cardDetailsTitle: 'Kartendaten eingeben',
    cardDetailsHint: 'Geben Sie unten Ihre Kartendaten ein, um die Zahlung abzuschließen.',
    amountToPay: 'Zu zahlender Betrag',
  },
  it: {
    payment: 'Pagamento',
    cardPayment: 'Pagamento con carta',
    tableNumber: 'Numero del tavolo',
    orderNumber: 'Numero dell’ordine',
    date: 'Data',
    time: 'Ora',
    menu: 'Menu',
    qty: 'Qtà',
    price: 'Prezzo',
    selectTip: 'Seleziona mancia per il cameriere',
    customInput: 'ALTRO',
    percentage: '%',
    amount: 'Importo',
    enterTip: 'Inserisci mancia',
    enterTipPercentage: 'Inserisci percentuale della mancia',
    enterTipAmount: 'Inserisci importo della mancia',
    estimatedTip: 'Mancia stimata',
    cancel: 'Annulla',
    points: 'Punti',
    available: 'Disponibile',
    pointsSection: 'Usa punti',
    pointsAvailable: 'Punti disponibili',
    pointsUsable: 'Utilizzabili',
    pointsToUse: 'Punti da usare',
    pointsDiscount: 'Sconto punti',
    pointsDiscountApplied: 'sconto applicato',
    pointsMinAmount: 'Importo minimo:',
    loginToSeePoints: 'Accedi per usare i punti',
    pointsLoadError: 'Caricamento delle informazioni sui punti fallito',
    estimatedPoints: 'Punti da guadagnare',
    estimating: 'Calcolo...',
    menuFirst: 'Usa prima il menu',
    use: 'Usa',
    useAll: 'Usa tutto',
    alwaysUseAll: 'Usa sempre tutto',
    giftCard: 'Carta regalo',
    enterCode: 'Inserisci il codice',
    apply: 'Applica',
    availablePoints: 'Hai punti disponibili',
    registerNewCard: 'Registra nuova carta',
    subtotal: 'Subtotale con tasse',
    netPrice: 'Prezzo netto',
    tax: 'Tassa',
    tip: 'Mancia',
    total: 'Totale',
    orderSummary: 'Riepilogo ordine',
    makePayment: 'Effettua pagamento',
    processing: 'Elaborazione del pagamento...',
    processingMessage: 'Attendere prego mentre elaboriamo il pagamento...',
    paymentSuccess: 'Pagamento riuscito!',
    paymentFailed: 'Pagamento fallito',
    paymentFailedMessage: 'Qualcosa è andato storto con il tuo pagamento. Riprova.',
    closeAndContinue: 'Chiudi',
    tryAgain: 'Riprova',
    selectCardError: 'Per favore seleziona una carta di pagamento',
    paymentMethod: 'Metodo di pagamento',
    storeNotFound: 'Informazioni del negozio non trovate.',
    noOrderableItems: 'Nessun articolo disponibile per l’ordine.',
    mixedStoreError: 'Il carrello contiene articoli di negozi diversi.',
    goBack: 'Indietro',
    cardDetailsTitle: 'Inserisci i dettagli della carta',
    cardDetailsHint: 'Inserisci i dettagli della tua carta qui sotto per completare il pagamento.',
    amountToPay: 'Importo da pagare',
  },
  ru: {
    payment: 'Оплата',
    cardPayment: 'Оплата картой',
    tableNumber: 'Номер столика',
    orderNumber: 'Номер заказа',
    date: 'Дата',
    time: 'Время',
    menu: 'Меню',
    qty: 'Кол-во',
    price: 'Цена',
    selectTip: 'Выбрать чаевые для официанта',
    customInput: 'ДРУГОЕ',
    percentage: '%',
    amount: 'Сумма',
    enterTip: 'Ввести чаевые',
    enterTipPercentage: 'Введите процент чаевых',
    enterTipAmount: 'Введите сумму чаевых',
    estimatedTip: 'Ожидаемые чаевые',
    cancel: 'Отмена',
    points: 'Очки',
    available: 'Доступно',
    pointsSection: 'Использовать очки',
    pointsAvailable: 'Доступные очки',
    pointsUsable: 'Можно использовать',
    pointsToUse: 'Очки к использованию',
    pointsDiscount: 'Скидка очками',
    pointsDiscountApplied: 'скидка применена',
    pointsMinAmount: 'Минимальная сумма:',
    loginToSeePoints: 'Войдите, чтобы использовать очки',
    pointsLoadError: 'Не удалось загрузить информацию об очках',
    estimatedPoints: 'Очки к начислению',
    estimating: 'Расчёт...',
    menuFirst: 'Сначала использовать меню',
    use: 'Использовать',
    useAll: 'Использовать всё',
    alwaysUseAll: 'Всегда использовать всё',
    giftCard: 'Подарочная карта',
    enterCode: 'Введите код',
    apply: 'Применить',
    availablePoints: 'У вас есть доступные очки',
    registerNewCard: 'Зарегистрировать новую карту',
    subtotal: 'Промежуточная сумма с налогом',
    netPrice: 'Чистая цена',
    tax: 'Налог',
    tip: 'Чаевые',
    total: 'Итого',
    orderSummary: 'Сводка заказа',
    makePayment: 'Произвести оплату',
    processing: 'Обработка платежа...',
    processingMessage: 'Пожалуйста, подождите, пока мы обрабатываем ваш платёж...',
    paymentSuccess: 'Оплата прошла успешно!',
    paymentFailed: 'Оплата не удалась',
    paymentFailedMessage: 'Что-то пошло не так с вашим платежом. Пожалуйста, повторите попытку.',
    closeAndContinue: 'Закрыть',
    tryAgain: 'Повторить',
    selectCardError: 'Пожалуйста, выберите платёжную карту',
    paymentMethod: 'Способ оплаты',
    storeNotFound: 'Информация о магазине не найдена.',
    noOrderableItems: 'Нет доступных для заказа товаров.',
    mixedStoreError: 'Корзина содержит товары из разных магазинов.',
    goBack: 'Назад',
    cardDetailsTitle: 'Введите данные карты',
    cardDetailsHint: 'Введите данные карты ниже, чтобы завершить оплату.',
    amountToPay: 'Сумма к оплате',
  },
  ar: {
    payment: 'الدفع',
    cardPayment: 'الدفع بالبطاقة',
    tableNumber: 'رقم الطاولة',
    orderNumber: 'رقم الطلب',
    date: 'التاريخ',
    time: 'الوقت',
    menu: 'القائمة',
    qty: 'الكمية',
    price: 'السعر',
    selectTip: 'اختر إكرامية للنادل',
    customInput: 'آخر',
    percentage: '%',
    amount: 'المبلغ',
    enterTip: 'أدخل الإكرامية',
    enterTipPercentage: 'أدخل نسبة الإكرامية',
    enterTipAmount: 'أدخل مبلغ الإكرامية',
    estimatedTip: 'الإكرامية المقدرة',
    cancel: 'إلغاء',
    points: 'النقاط',
    available: 'متوفر',
    pointsSection: 'استخدام النقاط',
    pointsAvailable: 'النقاط المتاحة',
    pointsUsable: 'قابل للاستخدام',
    pointsToUse: 'النقاط المراد استخدامها',
    pointsDiscount: 'خصم النقاط',
    pointsDiscountApplied: 'تم تطبيق الخصم',
    pointsMinAmount: 'الحد الأدنى للمبلغ:',
    loginToSeePoints: 'سجّل الدخول لاستخدام النقاط',
    pointsLoadError: 'فشل في تحميل معلومات النقاط',
    estimatedPoints: 'النقاط المكتسبة المتوقعة',
    estimating: 'جاري الحساب...',
    menuFirst: 'استخدم القائمة أولاً',
    use: 'استخدام',
    useAll: 'استخدام الكل',
    alwaysUseAll: 'استخدام الكل دائماً',
    giftCard: 'بطاقة هدية',
    enterCode: 'أدخل الرمز',
    apply: 'تطبيق',
    availablePoints: 'لديك نقاط متاحة',
    registerNewCard: 'تسجيل بطاقة جديدة',
    subtotal: 'المجموع الفرعي مع الضريبة',
    netPrice: 'السعر الصافي',
    tax: 'الضريبة',
    tip: 'الإكرامية',
    total: 'الإجمالي',
    orderSummary: 'ملخص الطلب',
    makePayment: 'إجراء الدفع',
    processing: 'جاري معالجة الدفع...',
    processingMessage: 'يرجى الانتظار بينما نعالج دفعك...',
    paymentSuccess: 'تم الدفع بنجاح!',
    paymentFailed: 'فشل الدفع',
    paymentFailedMessage: 'حدث خطأ ما في الدفع. يرجى المحاولة مرة أخرى.',
    closeAndContinue: 'إغلاق',
    tryAgain: 'حاول مرة أخرى',
    selectCardError: 'يرجى اختيار بطاقة الدفع',
    paymentMethod: 'طريقة الدفع',
    storeNotFound: 'لم يتم العثور على معلومات المتجر.',
    noOrderableItems: 'لا توجد عناصر متاحة للطلب.',
    mixedStoreError: 'تحتوي السلة على عناصر من متاجر مختلفة.',
    goBack: 'رجوع',
    cardDetailsTitle: 'أدخل بيانات البطاقة',
    cardDetailsHint: 'أدخل بيانات بطاقتك أدناه لإتمام الدفع.',
    amountToPay: 'المبلغ المستحق',
  },
  vi: {
    payment: 'Thanh toán',
    cardPayment: 'Thanh toán bằng thẻ',
    tableNumber: 'Số bàn',
    orderNumber: 'Số đơn hàng',
    date: 'Ngày',
    time: 'Giờ',
    menu: 'Menu',
    qty: 'SL',
    price: 'Giá',
    selectTip: 'Chọn tiền boa cho người phục vụ',
    customInput: 'KHÁC',
    percentage: '%',
    amount: 'Số tiền',
    enterTip: 'Nhập tiền boa',
    enterTipPercentage: 'Nhập tỷ lệ tiền boa',
    enterTipAmount: 'Nhập số tiền boa',
    estimatedTip: 'Tiền boa ước tính',
    cancel: 'Hủy',
    points: 'Điểm',
    available: 'Có sẵn',
    pointsSection: 'Sử dụng điểm',
    pointsAvailable: 'Điểm có sẵn',
    pointsUsable: 'Có thể dùng',
    pointsToUse: 'Điểm sử dụng',
    pointsDiscount: 'Giảm giá bằng điểm',
    pointsDiscountApplied: 'đã áp dụng giảm giá',
    pointsMinAmount: 'Số tiền tối thiểu:',
    loginToSeePoints: 'Đăng nhập để dùng điểm',
    pointsLoadError: 'Tải thông tin điểm thất bại',
    estimatedPoints: 'Điểm sẽ nhận',
    estimating: 'Đang tính...',
    menuFirst: 'Sử dụng menu trước',
    use: 'Sử dụng',
    useAll: 'Sử dụng tất cả',
    alwaysUseAll: 'Luôn sử dụng tất cả',
    giftCard: 'Thẻ quà tặng',
    enterCode: 'Nhập mã',
    apply: 'Áp dụng',
    availablePoints: 'Bạn có điểm có sẵn',
    registerNewCard: 'Đăng ký thẻ mới',
    subtotal: 'Tổng phụ bao gồm thuế',
    netPrice: 'Giá ròng',
    tax: 'Thuế',
    tip: 'Tiền boa',
    total: 'Tổng cộng',
    orderSummary: 'Tóm tắt đơn hàng',
    makePayment: 'Thực hiện thanh toán',
    processing: 'Đang xử lý thanh toán...',
    processingMessage: 'Vui lòng đợi trong khi chúng tôi xử lý thanh toán của bạn...',
    paymentSuccess: 'Thanh toán thành công!',
    paymentFailed: 'Thanh toán thất bại',
    paymentFailedMessage: 'Có lỗi xảy ra với thanh toán của bạn. Vui lòng thử lại.',
    closeAndContinue: 'Đóng',
    tryAgain: 'Thử lại',
    selectCardError: 'Vui lòng chọn thẻ thanh toán',
    paymentMethod: 'Phương thức thanh toán',
    storeNotFound: 'Không tìm thấy thông tin cửa hàng.',
    noOrderableItems: 'Không có mục nào có thể đặt hàng.',
    mixedStoreError: 'Giỏ hàng chứa các mục từ các cửa hàng khác nhau.',
    goBack: 'Quay lại',
    cardDetailsTitle: 'Nhập thông tin thẻ',
    cardDetailsHint: 'Nhập thông tin thẻ của bạn bên dưới để hoàn tất thanh toán.',
    amountToPay: 'Số tiền thanh toán',
  },
  th: {
    payment: 'การชำระเงิน',
    cardPayment: 'ชำระด้วยบัตร',
    tableNumber: 'หมายเลขโต๊ะ',
    orderNumber: 'หมายเลขคำสั่งซื้อ',
    date: 'วันที่',
    time: 'เวลา',
    menu: 'เมนู',
    qty: 'จำนวน',
    price: 'ราคา',
    selectTip: 'เลือกทิปสำหรับพนักงาน',
    customInput: 'อื่นๆ',
    percentage: '%',
    amount: 'จำนวน',
    enterTip: 'ป้อนทิป',
    enterTipPercentage: 'ป้อนเปอร์เซ็นต์ทิป',
    enterTipAmount: 'ป้อนจำนวนทิป',
    estimatedTip: 'ทิปโดยประมาณ',
    cancel: 'ยกเลิก',
    points: 'คะแนน',
    available: 'มีอยู่',
    pointsSection: 'ใช้คะแนน',
    pointsAvailable: 'คะแนนที่ใช้ได้',
    pointsUsable: 'ใช้ได้',
    pointsToUse: 'คะแนนที่จะใช้',
    pointsDiscount: 'ส่วนลดคะแนน',
    pointsDiscountApplied: 'ใช้ส่วนลดแล้ว',
    pointsMinAmount: 'จำนวนขั้นต่ำ:',
    loginToSeePoints: 'เข้าสู่ระบบเพื่อใช้คะแนน',
    pointsLoadError: 'โหลดข้อมูลคะแนนล้มเหลว',
    estimatedPoints: 'คะแนนที่จะได้รับ',
    estimating: 'กำลังคำนวณ...',
    menuFirst: 'ใช้เมนูก่อน',
    use: 'ใช้',
    useAll: 'ใช้ทั้งหมด',
    alwaysUseAll: 'ใช้ทั้งหมดเสมอ',
    giftCard: 'บัตรของขวัญ',
    enterCode: 'ป้อนรหัส',
    apply: 'ใช้',
    availablePoints: 'คุณมีคะแนนที่ใช้ได้',
    registerNewCard: 'ลงทะเบียนบัตรใหม่',
    subtotal: 'ยอดรวมย่อยพร้อมภาษี',
    netPrice: 'ราคาสุทธิ',
    tax: 'ภาษี',
    tip: 'ทิป',
    total: 'ยอดรวม',
    orderSummary: 'สรุปคำสั่งซื้อ',
    makePayment: 'ทำการชำระเงิน',
    processing: 'กำลังประมวลผลการชำระเงิน...',
    processingMessage: 'โปรดรอสักครู่ขณะที่เราประมวลผลการชำระเงินของคุณ...',
    paymentSuccess: 'ชำระเงินสำเร็จ!',
    paymentFailed: 'การชำระเงินล้มเหลว',
    paymentFailedMessage: 'เกิดข้อผิดพลาดกับการชำระเงินของคุณ โปรดลองอีกครั้ง',
    closeAndContinue: 'ปิด',
    tryAgain: 'ลองอีกครั้ง',
    selectCardError: 'โปรดเลือกบัตรชำระเงิน',
    paymentMethod: 'วิธีการชำระเงิน',
    storeNotFound: 'ไม่พบข้อมูลร้านค้า',
    noOrderableItems: 'ไม่มีรายการที่สั่งซื้อได้',
    mixedStoreError: 'ตะกร้ามีรายการจากร้านค้าที่แตกต่างกัน',
    goBack: 'กลับ',
    cardDetailsTitle: 'กรอกข้อมูลบัตร',
    cardDetailsHint: 'กรอกข้อมูลบัตรของคุณด้านล่างเพื่อทำการชำระเงินให้เสร็จสิ้น',
    amountToPay: 'จำนวนเงินที่ต้องชำระ',
  },
  nl: {
    payment: 'Betaling',
    cardPayment: 'Kaartbetaling',
    tableNumber: 'Tafelnummer',
    orderNumber: 'Bestelnummer',
    date: 'Datum',
    time: 'Tijd',
    menu: 'Menu',
    qty: 'Aantal',
    price: 'Prijs',
    selectTip: 'Selecteer fooi voor de bediening',
    customInput: 'ANDERE',
    percentage: '%',
    amount: 'Bedrag',
    enterTip: 'Fooi invoeren',
    enterTipPercentage: 'Voer fooipercentage in',
    enterTipAmount: 'Voer fooibedrag in',
    estimatedTip: 'Geschatte fooi',
    cancel: 'Annuleren',
    points: 'Punten',
    available: 'Beschikbaar',
    pointsSection: 'Punten gebruiken',
    pointsAvailable: 'Beschikbare punten',
    pointsUsable: 'Bruikbaar',
    pointsToUse: 'Te gebruiken punten',
    pointsDiscount: 'Puntenkorting',
    pointsDiscountApplied: 'korting toegepast',
    pointsMinAmount: 'Minimumbedrag:',
    loginToSeePoints: 'Log in om punten te gebruiken',
    pointsLoadError: 'Laden van puntinformatie mislukt',
    estimatedPoints: 'Te verdienen punten',
    estimating: 'Berekenen...',
    menuFirst: 'Eerst menu gebruiken',
    use: 'Gebruiken',
    useAll: 'Alles gebruiken',
    alwaysUseAll: 'Altijd alles gebruiken',
    giftCard: 'Cadeaubon',
    enterCode: 'Voer code in',
    apply: 'Toepassen',
    availablePoints: 'U heeft beschikbare punten',
    registerNewCard: 'Nieuwe kaart registreren',
    subtotal: 'Subtotaal incl. belasting',
    netPrice: 'Netto prijs',
    tax: 'Belasting',
    tip: 'Fooi',
    total: 'Totaal',
    orderSummary: 'Bestelling samenvatting',
    makePayment: 'Betaling uitvoeren',
    processing: 'Betaling wordt verwerkt...',
    processingMessage: 'Even geduld terwijl we uw betaling verwerken...',
    paymentSuccess: 'Betaling geslaagd!',
    paymentFailed: 'Betaling mislukt',
    paymentFailedMessage: 'Er is iets misgegaan met uw betaling. Probeer het opnieuw.',
    closeAndContinue: 'Sluiten',
    tryAgain: 'Opnieuw proberen',
    selectCardError: 'Selecteer een betaalkaart',
    paymentMethod: 'Betaalmethode',
    storeNotFound: 'Winkelinformatie niet gevonden.',
    noOrderableItems: 'Geen bestelbare items beschikbaar.',
    mixedStoreError: 'Winkelwagen bevat items van verschillende winkels.',
    goBack: 'Terug',
    cardDetailsTitle: 'Voer kaartgegevens in',
    cardDetailsHint: 'Voer hieronder uw kaartgegevens in om de betaling te voltooien.',
    amountToPay: 'Te betalen bedrag',
  },
};

/**
 * 후불 "지금 결제" 전용 번역 (C3 #590, T4) — 15언어 전수.
 *
 * cardInput(카드 폼 라벨)은 `bloominPaymentTranslations` 의 자산을 그대로 재사용하고,
 * 본 번역은 진입 버튼 / 모달 제목·설명 / 코드별 에러 문구만 다룬다.
 */
export const payNowTranslations: PayNowTranslations = {
  ko: {
    button: '지금 결제',
    modalTitle: '지금 결제',
    modalDescription: '미결제 주문을 카드로 결제합니다.',
    processing: '결제 처리 중...',
    session: {
      bannerSummary: '이 세션 미결제 {count}건 · {amount}',
      payAllButton: '전체 결제',
      modalTitle: '전체 결제',
      modalDescription: '미결제 {count}건을 한 번에 결제합니다.',
      recoveryNotice: '세션 정보를 찾을 수 없어 결제를 진행할 수 없어요. 테이블 QR 코드를 다시 스캔해 주세요.',
      progressLabel: '진행 중 {count}건',
      payButton: '결제하기',
      sheetTitle: '우리 테이블 주문',
      sheetTotalLabel: '합계',
      sheetPayButton: '전체 결제하기',
      sheetEmpty: '표시할 주문이 없어요.',
      sheetClose: '닫기',
      chargeBack: '뒤로',
      orderSubtotal: '소계',
    },
    adjustments: {
      sectionTitle: '추가 결제',
      tip: {
        label: '서버 팁 선택',
        customLabel: '입력',
        amountLabel: '예상 팁',
        enterTip: '팁 입력',
        percentage: '%',
        amount: '금액',
        enterTipPercentage: '팁 비율을 입력하세요',
        enterTipAmount: '팁 금액을 입력하세요',
        estimatedTip: '예상 팁',
        cancel: '취소',
        apply: '적용',
      },
      points: {
        sectionTitle: '포인트 사용',
        available: '보유 포인트',
        usable: '사용 가능',
        placeholder: '사용할 포인트',
        apply: '적용',
        useAll: '전액사용',
        discountApplied: '할인 적용됨',
        minAmount: '최소 사용 금액:',
        loginPrompt: '포인트를 사용하려면 로그인하세요',
      },
      coupon: {
        title: '쿠폰',
        none: '쿠폰 미사용',
        minOrderNotMet: '최소 주문액 미달',
        expired: '만료됨',
        notUsable: '사용할 수 없는 쿠폰',
      },
      preview: {
        title: '결제 금액',
        orderTotal: '주문 금액',
        tip: '팁',
        points: '포인트',
        coupon: '쿠폰',
        chargeAmount: '청구 금액',
      },
      zeroChargeConfirm: '전액 결제 확정',
    },
    errors: {
      notSettleable: '이 주문은 지금 결제할 수 없습니다.',
      processingCannotCancel: '결제가 처리 중입니다. 잠시 후 다시 시도해 주세요.',
      untrustedChallenge: '결제 인증 페이지를 열 수 없습니다.',
      adjustmentValidationFailed: '적용한 할인 또는 포인트를 사용할 수 없습니다. 다시 확인해 주세요.',
      cardRequiredForCharge: '결제할 금액이 남아 있어 카드 정보가 필요합니다.',
      generic: '결제에 실패했습니다. 다시 시도해 주세요.',
    },
  },
  es: {
    button: 'Pagar ahora',
    modalTitle: 'Pagar ahora',
    modalDescription: 'Paga con tarjeta tu pedido pendiente.',
    processing: 'Procesando el pago...',
    session: {
      bannerSummary: '{count} pedidos sin pagar en esta sesión · {amount}',
      payAllButton: 'Pagar todo',
      modalTitle: 'Pagar todo',
      modalDescription: 'Paga {count} pedidos pendientes de una sola vez.',
      recoveryNotice: 'No encontramos los datos de la sesión y no podemos procesar el pago. Vuelve a escanear el código QR de la mesa.',
      progressLabel: '{count} en curso',
      payButton: 'Pagar',
      sheetTitle: 'Pedido de nuestra mesa',
      sheetTotalLabel: 'Total',
      sheetPayButton: 'Pagar todo',
      sheetEmpty: 'No hay pedidos para mostrar.',
      sheetClose: 'Cerrar',
      chargeBack: 'Atrás',
      orderSubtotal: 'Subtotal',
    },
    adjustments: {
      sectionTitle: 'Pago adicional',
      tip: {
        label: 'Seleccionar propina para el servidor',
        customLabel: 'OTRO',
        amountLabel: 'Propina estimada',
        enterTip: 'Ingresar propina',
        percentage: '%',
        amount: 'Cantidad',
        enterTipPercentage: 'Ingresa porcentaje de propina',
        enterTipAmount: 'Ingresa cantidad de propina',
        estimatedTip: 'Propina estimada',
        cancel: 'Cancelar',
        apply: 'Aplicar',
      },
      points: {
        sectionTitle: 'Usar puntos',
        available: 'Puntos disponibles',
        usable: 'Disponible',
        placeholder: 'Puntos a usar',
        apply: 'Aplicar',
        useAll: 'Usar todo',
        discountApplied: 'descuento aplicado',
        minAmount: 'Monto mínimo:',
        loginPrompt: 'Inicia sesión para usar puntos',
      },
      coupon: {
        title: 'Cupón',
        none: 'Sin cupón',
        minOrderNotMet: 'No alcanza el monto mínimo',
        expired: 'Vencido',
        notUsable: 'Cupón no disponible',
      },
      preview: {
        title: 'Monto a pagar',
        orderTotal: 'Total del pedido',
        tip: 'Propina',
        points: 'Puntos',
        coupon: 'Cupón',
        chargeAmount: 'Monto a cobrar',
      },
      zeroChargeConfirm: 'Confirmar pago total',
    },
    errors: {
      notSettleable: 'Este pedido no se puede pagar en este momento.',
      processingCannotCancel: 'El pago se está procesando. Inténtalo de nuevo en unos momentos.',
      untrustedChallenge: 'No se pudo abrir la página de autenticación del pago.',
      adjustmentValidationFailed: 'No se pudieron aplicar los descuentos o puntos. Verifícalos de nuevo.',
      cardRequiredForCharge: 'Queda un monto por pagar, se requiere una tarjeta.',
      generic: 'El pago falló. Por favor intenta de nuevo.',
    },
  },
  en: {
    button: 'Pay now',
    modalTitle: 'Pay now',
    modalDescription: 'Pay for your pending order by card.',
    processing: 'Processing payment...',
    session: {
      bannerSummary: '{count} unpaid orders in this session · {amount}',
      payAllButton: 'Pay all',
      modalTitle: 'Pay all',
      modalDescription: 'Pay {count} pending orders at once.',
      recoveryNotice: "We couldn't find your session, so payment can't proceed. Please scan the table QR code again.",
      progressLabel: '{count} in progress',
      payButton: 'Pay',
      sheetTitle: 'Our table order',
      sheetTotalLabel: 'Total',
      sheetPayButton: 'Pay all',
      sheetEmpty: 'No orders to show.',
      sheetClose: 'Close',
      chargeBack: 'Back',
      orderSubtotal: 'Subtotal',
    },
    adjustments: {
      sectionTitle: 'Additional payment',
      tip: {
        label: 'Select tip for server',
        customLabel: 'OTHER',
        amountLabel: 'Estimated tip',
        enterTip: 'Enter tip',
        percentage: '%',
        amount: 'Amount',
        enterTipPercentage: 'Enter tip percentage',
        enterTipAmount: 'Enter tip amount',
        estimatedTip: 'Estimated tip',
        cancel: 'Cancel',
        apply: 'Apply',
      },
      points: {
        sectionTitle: 'Use points',
        available: 'Available points',
        usable: 'Usable',
        placeholder: 'Points to use',
        apply: 'Apply',
        useAll: 'Use all',
        discountApplied: 'discount applied',
        minAmount: 'Minimum amount:',
        loginPrompt: 'Log in to use points',
      },
      coupon: {
        title: 'Coupon',
        none: 'No coupon',
        minOrderNotMet: 'Minimum order not met',
        expired: 'Expired',
        notUsable: 'Coupon not available',
      },
      preview: {
        title: 'Amount to pay',
        orderTotal: 'Order total',
        tip: 'Tip',
        points: 'Points',
        coupon: 'Coupon',
        chargeAmount: 'Amount to charge',
      },
      zeroChargeConfirm: 'Confirm full payment',
    },
    errors: {
      notSettleable: 'This order cannot be paid right now.',
      processingCannotCancel: 'The payment is being processed. Please try again in a few moments.',
      untrustedChallenge: 'The payment authentication page could not be opened.',
      adjustmentValidationFailed: 'The discounts or points could not be applied. Please check them again.',
      cardRequiredForCharge: 'There is an amount left to pay, a card is required.',
      generic: 'The payment failed. Please try again.',
    },
  },
  pt: {
    button: 'Pagar agora',
    modalTitle: 'Pagar agora',
    modalDescription: 'Pague seu pedido pendente com cartão.',
    processing: 'Processando o pagamento...',
    session: {
      bannerSummary: '{count} pedidos não pagos nesta sessão · {amount}',
      payAllButton: 'Pagar tudo',
      modalTitle: 'Pagar tudo',
      modalDescription: 'Pague {count} pedidos pendentes de uma só vez.',
      recoveryNotice: 'Não encontramos os dados da sessão e não é possível processar o pagamento. Escaneie novamente o QR code da mesa.',
      progressLabel: '{count} em andamento',
      payButton: 'Pagar',
      sheetTitle: 'Pedido da nossa mesa',
      sheetTotalLabel: 'Total',
      sheetPayButton: 'Pagar tudo',
      sheetEmpty: 'Nenhum pedido para mostrar.',
      sheetClose: 'Fechar',
      chargeBack: 'Voltar',
      orderSubtotal: 'Subtotal',
    },
    adjustments: {
      sectionTitle: 'Pagamento adicional',
      tip: {
        label: 'Selecionar gorjeta para o garçom',
        customLabel: 'OUTRO',
        amountLabel: 'Gorjeta estimada',
        enterTip: 'Inserir gorjeta',
        percentage: '%',
        amount: 'Valor',
        enterTipPercentage: 'Insira a porcentagem da gorjeta',
        enterTipAmount: 'Insira o valor da gorjeta',
        estimatedTip: 'Gorjeta estimada',
        cancel: 'Cancelar',
        apply: 'Aplicar',
      },
      points: {
        sectionTitle: 'Usar pontos',
        available: 'Pontos disponíveis',
        usable: 'Disponível',
        placeholder: 'Pontos a usar',
        apply: 'Aplicar',
        useAll: 'Usar tudo',
        discountApplied: 'desconto aplicado',
        minAmount: 'Valor mínimo:',
        loginPrompt: 'Faça login para usar pontos',
      },
      coupon: {
        title: 'Cupom',
        none: 'Sem cupom',
        minOrderNotMet: 'Valor mínimo não atingido',
        expired: 'Expirado',
        notUsable: 'Cupom indisponível',
      },
      preview: {
        title: 'Valor a pagar',
        orderTotal: 'Total do pedido',
        tip: 'Gorjeta',
        points: 'Pontos',
        coupon: 'Cupom',
        chargeAmount: 'Valor a cobrar',
      },
      zeroChargeConfirm: 'Confirmar pagamento total',
    },
    errors: {
      notSettleable: 'Este pedido não pode ser pago no momento.',
      processingCannotCancel: 'O pagamento está sendo processado. Tente novamente em alguns instantes.',
      untrustedChallenge: 'Não foi possível abrir a página de autenticação do pagamento.',
      adjustmentValidationFailed: 'Não foi possível aplicar os descontos ou pontos. Verifique novamente.',
      cardRequiredForCharge: 'Resta um valor a pagar, é necessário um cartão.',
      generic: 'O pagamento falhou. Por favor, tente novamente.',
    },
  },
  fr: {
    button: 'Payer maintenant',
    modalTitle: 'Payer maintenant',
    modalDescription: 'Payez votre commande en attente par carte.',
    processing: 'Traitement du paiement...',
    session: {
      bannerSummary: '{count} commandes impayées dans cette session · {amount}',
      payAllButton: 'Tout payer',
      modalTitle: 'Tout payer',
      modalDescription: 'Payez {count} commandes en attente en une seule fois.',
      recoveryNotice: "Nous n'avons pas trouvé votre session, le paiement est impossible. Veuillez scanner à nouveau le QR code de la table.",
      progressLabel: '{count} en cours',
      payButton: 'Payer',
      sheetTitle: 'Commande de notre table',
      sheetTotalLabel: 'Total',
      sheetPayButton: 'Tout payer',
      sheetEmpty: 'Aucune commande à afficher.',
      sheetClose: 'Fermer',
      chargeBack: 'Retour',
      orderSubtotal: 'Sous-total',
    },
    adjustments: {
      sectionTitle: 'Paiement supplémentaire',
      tip: {
        label: 'Sélectionner un pourboire pour le serveur',
        customLabel: 'AUTRE',
        amountLabel: 'Pourboire estimé',
        enterTip: 'Saisir le pourboire',
        percentage: '%',
        amount: 'Montant',
        enterTipPercentage: 'Saisir le pourcentage du pourboire',
        enterTipAmount: 'Saisir le montant du pourboire',
        estimatedTip: 'Pourboire estimé',
        cancel: 'Annuler',
        apply: 'Appliquer',
      },
      points: {
        sectionTitle: 'Utiliser des points',
        available: 'Points disponibles',
        usable: 'Utilisable',
        placeholder: 'Points à utiliser',
        apply: 'Appliquer',
        useAll: 'Tout utiliser',
        discountApplied: 'réduction appliquée',
        minAmount: 'Montant minimum:',
        loginPrompt: 'Connectez-vous pour utiliser les points',
      },
      coupon: {
        title: 'Coupon',
        none: 'Sans coupon',
        minOrderNotMet: 'Montant minimum non atteint',
        expired: 'Expiré',
        notUsable: 'Coupon indisponible',
      },
      preview: {
        title: 'Montant à payer',
        orderTotal: 'Total de la commande',
        tip: 'Pourboire',
        points: 'Points',
        coupon: 'Coupon',
        chargeAmount: 'Montant à débiter',
      },
      zeroChargeConfirm: 'Confirmer le paiement total',
    },
    errors: {
      notSettleable: 'Cette commande ne peut pas être payée pour le moment.',
      processingCannotCancel: 'Le paiement est en cours de traitement. Veuillez réessayer dans quelques instants.',
      untrustedChallenge: 'Impossible d’ouvrir la page d’authentification du paiement.',
      adjustmentValidationFailed: 'Les réductions ou les points n’ont pas pu être appliqués. Veuillez les vérifier de nouveau.',
      cardRequiredForCharge: 'Il reste un montant à payer, une carte est requise.',
      generic: 'Le paiement a échoué. Veuillez réessayer.',
    },
  },
  zh: {
    button: '立即支付',
    modalTitle: '立即支付',
    modalDescription: '使用银行卡支付您的未付订单。',
    processing: '正在处理支付...',
    session: {
      bannerSummary: '本次会话有 {count} 笔未付订单 · {amount}',
      payAllButton: '全部支付',
      modalTitle: '全部支付',
      modalDescription: '一次性支付 {count} 笔待付订单。',
      recoveryNotice: '未找到会话信息，无法继续付款。请重新扫描餐桌二维码。',
      progressLabel: '进行中 {count} 项',
      payButton: '去支付',
      sheetTitle: '我们这桌的订单',
      sheetTotalLabel: '合计',
      sheetPayButton: '全部支付',
      sheetEmpty: '没有可显示的订单。',
      sheetClose: '关闭',
      chargeBack: '返回',
      orderSubtotal: '小计',
    },
    adjustments: {
      sectionTitle: '附加支付',
      tip: {
        label: '选择服务员小费',
        customLabel: '其他',
        amountLabel: '预计小费',
        enterTip: '输入小费',
        percentage: '%',
        amount: '金额',
        enterTipPercentage: '输入小费百分比',
        enterTipAmount: '输入小费金额',
        estimatedTip: '预计小费',
        cancel: '取消',
        apply: '应用',
      },
      points: {
        sectionTitle: '使用积分',
        available: '可用积分',
        usable: '可用',
        placeholder: '要使用的积分',
        apply: '应用',
        useAll: '全部使用',
        discountApplied: '已应用折扣',
        minAmount: '最低金额:',
        loginPrompt: '登录以使用积分',
      },
      coupon: {
        title: '优惠券',
        none: '不使用优惠券',
        minOrderNotMet: '未达到最低订单金额',
        expired: '已过期',
        notUsable: '优惠券不可用',
      },
      preview: {
        title: '应付金额',
        orderTotal: '订单金额',
        tip: '小费',
        points: '积分',
        coupon: '优惠券',
        chargeAmount: '收款金额',
      },
      zeroChargeConfirm: '确认全额支付',
    },
    errors: {
      notSettleable: '此订单目前无法支付。',
      processingCannotCancel: '支付正在处理中。请稍后再试。',
      untrustedChallenge: '无法打开支付验证页面。',
      adjustmentValidationFailed: '无法应用折扣或积分。请重新确认。',
      cardRequiredForCharge: '仍有应付金额，需要银行卡。',
      generic: '支付失败。请重试。',
    },
  },
  ja: {
    button: '今すぐ支払う',
    modalTitle: '今すぐ支払う',
    modalDescription: '未払いの注文をカードで支払います。',
    processing: '支払い処理中...',
    session: {
      bannerSummary: 'このセッションの未払い {count} 件 · {amount}',
      payAllButton: 'まとめて支払う',
      modalTitle: 'まとめて支払う',
      modalDescription: '未払いの {count} 件をまとめて支払います。',
      recoveryNotice: 'セッション情報が見つからず、お支払いを進められません。テーブルのQRコードを再度スキャンしてください。',
      progressLabel: '進行中 {count}件',
      payButton: '支払う',
      sheetTitle: 'このテーブルの注文',
      sheetTotalLabel: '合計',
      sheetPayButton: 'まとめて支払う',
      sheetEmpty: '表示できる注文がありません。',
      sheetClose: '閉じる',
      chargeBack: '戻る',
      orderSubtotal: '小計',
    },
    adjustments: {
      sectionTitle: '追加のお支払い',
      tip: {
        label: 'スタッフへのチップを選択',
        customLabel: 'その他',
        amountLabel: '推定チップ',
        enterTip: 'チップを入力',
        percentage: '%',
        amount: '金額',
        enterTipPercentage: 'チップの割合を入力',
        enterTipAmount: 'チップの金額を入力',
        estimatedTip: '推定チップ',
        cancel: 'キャンセル',
        apply: '適用',
      },
      points: {
        sectionTitle: 'ポイントを使用',
        available: '利用可能ポイント',
        usable: '使用可能',
        placeholder: '使用するポイント',
        apply: '適用',
        useAll: '全額使用',
        discountApplied: '割引適用済み',
        minAmount: '最低金額:',
        loginPrompt: 'ポイントを使用するにはログインしてください',
      },
      coupon: {
        title: 'クーポン',
        none: 'クーポンを使用しない',
        minOrderNotMet: '最低注文金額に達していません',
        expired: '期限切れ',
        notUsable: '利用できないクーポン',
      },
      preview: {
        title: 'お支払い金額',
        orderTotal: '注文金額',
        tip: 'チップ',
        points: 'ポイント',
        coupon: 'クーポン',
        chargeAmount: '請求金額',
      },
      zeroChargeConfirm: '全額支払いを確定',
    },
    errors: {
      notSettleable: 'この注文は現在お支払いいただけません。',
      processingCannotCancel: '支払いを処理中です。しばらくしてからもう一度お試しください。',
      untrustedChallenge: '支払い認証ページを開けませんでした。',
      adjustmentValidationFailed: '割引またはポイントを適用できませんでした。もう一度ご確認ください。',
      cardRequiredForCharge: 'お支払い金額が残っているため、カード情報が必要です。',
      generic: '支払いに失敗しました。もう一度お試しください。',
    },
  },
  hi: {
    button: 'अभी भुगतान करें',
    modalTitle: 'अभी भुगतान करें',
    modalDescription: 'अपने बकाया ऑर्डर का कार्ड से भुगतान करें।',
    processing: 'भुगतान संसाधित हो रहा है...',
    session: {
      bannerSummary: 'इस सत्र में {count} अवैतनिक ऑर्डर · {amount}',
      payAllButton: 'सभी का भुगतान करें',
      modalTitle: 'सभी का भुगतान करें',
      modalDescription: 'एक साथ {count} लंबित ऑर्डर का भुगतान करें।',
      recoveryNotice: 'सत्र की जानकारी नहीं मिली, भुगतान आगे नहीं बढ़ सकता। कृपया टेबल का QR कोड फिर से स्कैन करें।',
      progressLabel: '{count} चल रहे हैं',
      payButton: 'भुगतान करें',
      sheetTitle: 'हमारी टेबल का ऑर्डर',
      sheetTotalLabel: 'कुल',
      sheetPayButton: 'सभी का भुगतान करें',
      sheetEmpty: 'दिखाने के लिए कोई ऑर्डर नहीं है।',
      sheetClose: 'बंद करें',
      chargeBack: 'वापस',
      orderSubtotal: 'उप-योग',
    },
    adjustments: {
      sectionTitle: 'अतिरिक्त भुगतान',
      tip: {
        label: 'सर्वर के लिए टिप चुनें',
        customLabel: 'अन्य',
        amountLabel: 'अनुमानित टिप',
        enterTip: 'टिप दर्ज करें',
        percentage: '%',
        amount: 'राशि',
        enterTipPercentage: 'टिप प्रतिशत दर्ज करें',
        enterTipAmount: 'टिप राशि दर्ज करें',
        estimatedTip: 'अनुमानित टिप',
        cancel: 'रद्द करें',
        apply: 'लागू करें',
      },
      points: {
        sectionTitle: 'अंक उपयोग करें',
        available: 'उपलब्ध अंक',
        usable: 'उपयोग योग्य',
        placeholder: 'उपयोग करने के अंक',
        apply: 'लागू करें',
        useAll: 'सब उपयोग करें',
        discountApplied: 'छूट लागू',
        minAmount: 'न्यूनतम राशि:',
        loginPrompt: 'अंक उपयोग करने के लिए लॉग इन करें',
      },
      coupon: {
        title: 'कूपन',
        none: 'कोई कूपन नहीं',
        minOrderNotMet: 'न्यूनतम ऑर्डर राशि पूरी नहीं',
        expired: 'समाप्त',
        notUsable: 'कूपन उपलब्ध नहीं',
      },
      preview: {
        title: 'भुगतान राशि',
        orderTotal: 'ऑर्डर कुल',
        tip: 'टिप',
        points: 'अंक',
        coupon: 'कूपन',
        chargeAmount: 'वसूली राशि',
      },
      zeroChargeConfirm: 'पूर्ण भुगतान की पुष्टि करें',
    },
    errors: {
      notSettleable: 'इस ऑर्डर का अभी भुगतान नहीं किया जा सकता।',
      processingCannotCancel: 'भुगतान संसाधित हो रहा है। कृपया कुछ क्षण बाद पुनः प्रयास करें।',
      untrustedChallenge: 'भुगतान प्रमाणीकरण पृष्ठ नहीं खोला जा सका।',
      adjustmentValidationFailed: 'छूट या अंक लागू नहीं किए जा सके। कृपया उन्हें फिर से जाँचें।',
      cardRequiredForCharge: 'भुगतान के लिए राशि शेष है, कार्ड आवश्यक है।',
      generic: 'भुगतान विफल रहा। कृपया पुनः प्रयास करें।',
    },
  },
  de: {
    button: 'Jetzt bezahlen',
    modalTitle: 'Jetzt bezahlen',
    modalDescription: 'Bezahlen Sie Ihre offene Bestellung per Karte.',
    processing: 'Zahlung wird verarbeitet...',
    session: {
      bannerSummary: '{count} unbezahlte Bestellungen in dieser Sitzung · {amount}',
      payAllButton: 'Alles bezahlen',
      modalTitle: 'Alles bezahlen',
      modalDescription: 'Bezahlen Sie {count} offene Bestellungen auf einmal.',
      recoveryNotice: 'Wir konnten Ihre Sitzung nicht finden, die Zahlung ist nicht möglich. Bitte scannen Sie den QR-Code am Tisch erneut.',
      progressLabel: '{count} in Bearbeitung',
      payButton: 'Bezahlen',
      sheetTitle: 'Bestellung unseres Tisches',
      sheetTotalLabel: 'Gesamt',
      sheetPayButton: 'Alles bezahlen',
      sheetEmpty: 'Keine Bestellungen anzuzeigen.',
      sheetClose: 'Schließen',
      chargeBack: 'Zurück',
      orderSubtotal: 'Zwischensumme',
    },
    adjustments: {
      sectionTitle: 'Zusätzliche Zahlung',
      tip: {
        label: 'Trinkgeld für Server auswählen',
        customLabel: 'ANDERE',
        amountLabel: 'Geschätztes Trinkgeld',
        enterTip: 'Trinkgeld eingeben',
        percentage: '%',
        amount: 'Betrag',
        enterTipPercentage: 'Trinkgeldprozentsatz eingeben',
        enterTipAmount: 'Trinkgeldbetrag eingeben',
        estimatedTip: 'Geschätztes Trinkgeld',
        cancel: 'Abbrechen',
        apply: 'Anwenden',
      },
      points: {
        sectionTitle: 'Punkte verwenden',
        available: 'Verfügbare Punkte',
        usable: 'Nutzbar',
        placeholder: 'Zu verwendende Punkte',
        apply: 'Anwenden',
        useAll: 'Alles verwenden',
        discountApplied: 'Rabatt angewendet',
        minAmount: 'Mindestbetrag:',
        loginPrompt: 'Melden Sie sich an, um Punkte zu verwenden',
      },
      coupon: {
        title: 'Gutschein',
        none: 'Kein Gutschein',
        minOrderNotMet: 'Mindestbestellwert nicht erreicht',
        expired: 'Abgelaufen',
        notUsable: 'Gutschein nicht verfügbar',
      },
      preview: {
        title: 'Zu zahlender Betrag',
        orderTotal: 'Bestellsumme',
        tip: 'Trinkgeld',
        points: 'Punkte',
        coupon: 'Gutschein',
        chargeAmount: 'Abzubuchender Betrag',
      },
      zeroChargeConfirm: 'Vollständige Zahlung bestätigen',
    },
    errors: {
      notSettleable: 'Diese Bestellung kann derzeit nicht bezahlt werden.',
      processingCannotCancel: 'Die Zahlung wird verarbeitet. Bitte versuchen Sie es in wenigen Augenblicken erneut.',
      untrustedChallenge: 'Die Seite zur Zahlungsauthentifizierung konnte nicht geöffnet werden.',
      adjustmentValidationFailed: 'Die Rabatte oder Punkte konnten nicht angewendet werden. Bitte überprüfen Sie sie erneut.',
      cardRequiredForCharge: 'Es ist noch ein Betrag zu zahlen, eine Karte ist erforderlich.',
      generic: 'Die Zahlung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
    },
  },
  it: {
    button: 'Paga ora',
    modalTitle: 'Paga ora',
    modalDescription: 'Paga con carta il tuo ordine in sospeso.',
    processing: 'Elaborazione del pagamento...',
    session: {
      bannerSummary: '{count} ordini non pagati in questa sessione · {amount}',
      payAllButton: 'Paga tutto',
      modalTitle: 'Paga tutto',
      modalDescription: 'Paga {count} ordini in sospeso in una sola volta.',
      recoveryNotice: 'Non abbiamo trovato la tua sessione e il pagamento non può procedere. Scansiona di nuovo il codice QR del tavolo.',
      progressLabel: '{count} in corso',
      payButton: 'Paga',
      sheetTitle: 'Ordine del nostro tavolo',
      sheetTotalLabel: 'Totale',
      sheetPayButton: 'Paga tutto',
      sheetEmpty: 'Nessun ordine da mostrare.',
      sheetClose: 'Chiudi',
      chargeBack: 'Indietro',
      orderSubtotal: 'Subtotale',
    },
    adjustments: {
      sectionTitle: 'Pagamento aggiuntivo',
      tip: {
        label: 'Seleziona mancia per il cameriere',
        customLabel: 'ALTRO',
        amountLabel: 'Mancia stimata',
        enterTip: 'Inserisci mancia',
        percentage: '%',
        amount: 'Importo',
        enterTipPercentage: 'Inserisci percentuale della mancia',
        enterTipAmount: 'Inserisci importo della mancia',
        estimatedTip: 'Mancia stimata',
        cancel: 'Annulla',
        apply: 'Applica',
      },
      points: {
        sectionTitle: 'Usa punti',
        available: 'Punti disponibili',
        usable: 'Utilizzabili',
        placeholder: 'Punti da usare',
        apply: 'Applica',
        useAll: 'Usa tutto',
        discountApplied: 'sconto applicato',
        minAmount: 'Importo minimo:',
        loginPrompt: 'Accedi per usare i punti',
      },
      coupon: {
        title: 'Coupon',
        none: 'Nessun coupon',
        minOrderNotMet: 'Importo minimo non raggiunto',
        expired: 'Scaduto',
        notUsable: 'Coupon non disponibile',
      },
      preview: {
        title: 'Importo da pagare',
        orderTotal: 'Totale ordine',
        tip: 'Mancia',
        points: 'Punti',
        coupon: 'Coupon',
        chargeAmount: 'Importo da addebitare',
      },
      zeroChargeConfirm: 'Conferma pagamento totale',
    },
    errors: {
      notSettleable: 'Questo ordine non può essere pagato in questo momento.',
      processingCannotCancel: 'Il pagamento è in elaborazione. Riprova tra qualche istante.',
      untrustedChallenge: 'Impossibile aprire la pagina di autenticazione del pagamento.',
      adjustmentValidationFailed: 'Non è stato possibile applicare gli sconti o i punti. Verificali di nuovo.',
      cardRequiredForCharge: 'Rimane un importo da pagare, è richiesta una carta.',
      generic: 'Il pagamento non è andato a buon fine. Riprova.',
    },
  },
  ru: {
    button: 'Оплатить сейчас',
    modalTitle: 'Оплатить сейчас',
    modalDescription: 'Оплатите неоплаченный заказ картой.',
    processing: 'Обработка платежа...',
    session: {
      bannerSummary: 'Неоплаченных заказов в этой сессии: {count} · {amount}',
      payAllButton: 'Оплатить всё',
      modalTitle: 'Оплатить всё',
      modalDescription: 'Оплатите {count} ожидающих заказов разом.',
      recoveryNotice: 'Не удалось найти данные сессии, оплата невозможна. Пожалуйста, отсканируйте QR-код стола ещё раз.',
      progressLabel: 'В процессе: {count}',
      payButton: 'Оплатить',
      sheetTitle: 'Заказ нашего стола',
      sheetTotalLabel: 'Итого',
      sheetPayButton: 'Оплатить всё',
      sheetEmpty: 'Нет заказов для отображения.',
      sheetClose: 'Закрыть',
      chargeBack: 'Назад',
      orderSubtotal: 'Промежуточный итог',
    },
    adjustments: {
      sectionTitle: 'Дополнительная оплата',
      tip: {
        label: 'Выбрать чаевые для официанта',
        customLabel: 'ДРУГОЕ',
        amountLabel: 'Ожидаемые чаевые',
        enterTip: 'Ввести чаевые',
        percentage: '%',
        amount: 'Сумма',
        enterTipPercentage: 'Введите процент чаевых',
        enterTipAmount: 'Введите сумму чаевых',
        estimatedTip: 'Ожидаемые чаевые',
        cancel: 'Отмена',
        apply: 'Применить',
      },
      points: {
        sectionTitle: 'Использовать очки',
        available: 'Доступные очки',
        usable: 'Можно использовать',
        placeholder: 'Очки к использованию',
        apply: 'Применить',
        useAll: 'Использовать всё',
        discountApplied: 'скидка применена',
        minAmount: 'Минимальная сумма:',
        loginPrompt: 'Войдите, чтобы использовать очки',
      },
      coupon: {
        title: 'Купон',
        none: 'Без купона',
        minOrderNotMet: 'Минимальная сумма не достигнута',
        expired: 'Истёк',
        notUsable: 'Купон недоступен',
      },
      preview: {
        title: 'Сумма к оплате',
        orderTotal: 'Итого по заказу',
        tip: 'Чаевые',
        points: 'Очки',
        coupon: 'Купон',
        chargeAmount: 'Сумма списания',
      },
      zeroChargeConfirm: 'Подтвердить полную оплату',
    },
    errors: {
      notSettleable: 'Этот заказ сейчас нельзя оплатить.',
      processingCannotCancel: 'Платёж обрабатывается. Пожалуйста, повторите попытку через несколько мгновений.',
      untrustedChallenge: 'Не удалось открыть страницу аутентификации платежа.',
      adjustmentValidationFailed: 'Не удалось применить скидки или очки. Пожалуйста, проверьте их ещё раз.',
      cardRequiredForCharge: 'Осталась сумма к оплате, требуется карта.',
      generic: 'Платёж не прошёл. Пожалуйста, повторите попытку.',
    },
  },
  ar: {
    button: 'ادفع الآن',
    modalTitle: 'ادفع الآن',
    modalDescription: 'ادفع طلبك غير المدفوع بالبطاقة.',
    processing: 'جاري معالجة الدفع...',
    session: {
      bannerSummary: '{count} طلبات غير مدفوعة في هذه الجلسة · {amount}',
      payAllButton: 'ادفع الكل',
      modalTitle: 'ادفع الكل',
      modalDescription: 'ادفع {count} طلبات معلقة دفعة واحدة.',
      recoveryNotice: 'لم نتمكن من العثور على جلستك، ولا يمكن إتمام الدفع. يرجى إعادة مسح رمز QR الخاص بالطاولة.',
      progressLabel: 'قيد التنفيذ {count}',
      payButton: 'ادفع',
      sheetTitle: 'طلب طاولتنا',
      sheetTotalLabel: 'الإجمالي',
      sheetPayButton: 'ادفع الكل',
      sheetEmpty: 'لا توجد طلبات للعرض.',
      sheetClose: 'إغلاق',
      chargeBack: 'رجوع',
      orderSubtotal: 'المجموع الفرعي',
    },
    adjustments: {
      sectionTitle: 'دفع إضافي',
      tip: {
        label: 'اختر إكرامية للنادل',
        customLabel: 'آخر',
        amountLabel: 'الإكرامية المقدرة',
        enterTip: 'أدخل الإكرامية',
        percentage: '%',
        amount: 'المبلغ',
        enterTipPercentage: 'أدخل نسبة الإكرامية',
        enterTipAmount: 'أدخل مبلغ الإكرامية',
        estimatedTip: 'الإكرامية المقدرة',
        cancel: 'إلغاء',
        apply: 'تطبيق',
      },
      points: {
        sectionTitle: 'استخدام النقاط',
        available: 'النقاط المتاحة',
        usable: 'قابل للاستخدام',
        placeholder: 'النقاط المراد استخدامها',
        apply: 'تطبيق',
        useAll: 'استخدام الكل',
        discountApplied: 'تم تطبيق الخصم',
        minAmount: 'الحد الأدنى للمبلغ:',
        loginPrompt: 'سجّل الدخول لاستخدام النقاط',
      },
      coupon: {
        title: 'قسيمة',
        none: 'بدون قسيمة',
        minOrderNotMet: 'لم يتم بلوغ الحد الأدنى للطلب',
        expired: 'منتهية الصلاحية',
        notUsable: 'القسيمة غير متاحة',
      },
      preview: {
        title: 'المبلغ المستحق',
        orderTotal: 'إجمالي الطلب',
        tip: 'الإكرامية',
        points: 'النقاط',
        coupon: 'القسيمة',
        chargeAmount: 'المبلغ المراد خصمه',
      },
      zeroChargeConfirm: 'تأكيد الدفع الكامل',
    },
    errors: {
      notSettleable: 'لا يمكن دفع هذا الطلب في الوقت الحالي.',
      processingCannotCancel: 'يتم معالجة الدفع. يرجى المحاولة مرة أخرى بعد لحظات.',
      untrustedChallenge: 'تعذّر فتح صفحة التحقق من الدفع.',
      adjustmentValidationFailed: 'تعذّر تطبيق الخصومات أو النقاط. يرجى التحقق منها مرة أخرى.',
      cardRequiredForCharge: 'لا يزال هناك مبلغ مستحق، البطاقة مطلوبة.',
      generic: 'فشل الدفع. يرجى المحاولة مرة أخرى.',
    },
  },
  vi: {
    button: 'Thanh toán ngay',
    modalTitle: 'Thanh toán ngay',
    modalDescription: 'Thanh toán đơn hàng chưa thanh toán của bạn bằng thẻ.',
    processing: 'Đang xử lý thanh toán...',
    session: {
      bannerSummary: '{count} đơn chưa thanh toán trong phiên này · {amount}',
      payAllButton: 'Thanh toán tất cả',
      modalTitle: 'Thanh toán tất cả',
      modalDescription: 'Thanh toán {count} đơn đang chờ cùng một lúc.',
      recoveryNotice: 'Không tìm thấy thông tin phiên, không thể tiến hành thanh toán. Vui lòng quét lại mã QR của bàn.',
      progressLabel: 'Đang xử lý {count}',
      payButton: 'Thanh toán',
      sheetTitle: 'Đơn của bàn chúng ta',
      sheetTotalLabel: 'Tổng cộng',
      sheetPayButton: 'Thanh toán tất cả',
      sheetEmpty: 'Không có đơn nào để hiển thị.',
      sheetClose: 'Đóng',
      chargeBack: 'Quay lại',
      orderSubtotal: 'Tạm tính',
    },
    adjustments: {
      sectionTitle: 'Thanh toán bổ sung',
      tip: {
        label: 'Chọn tiền boa cho người phục vụ',
        customLabel: 'KHÁC',
        amountLabel: 'Tiền boa ước tính',
        enterTip: 'Nhập tiền boa',
        percentage: '%',
        amount: 'Số tiền',
        enterTipPercentage: 'Nhập tỷ lệ tiền boa',
        enterTipAmount: 'Nhập số tiền boa',
        estimatedTip: 'Tiền boa ước tính',
        cancel: 'Hủy',
        apply: 'Áp dụng',
      },
      points: {
        sectionTitle: 'Sử dụng điểm',
        available: 'Điểm có sẵn',
        usable: 'Có thể dùng',
        placeholder: 'Điểm sử dụng',
        apply: 'Áp dụng',
        useAll: 'Sử dụng tất cả',
        discountApplied: 'đã áp dụng giảm giá',
        minAmount: 'Số tiền tối thiểu:',
        loginPrompt: 'Đăng nhập để dùng điểm',
      },
      coupon: {
        title: 'Phiếu giảm giá',
        none: 'Không dùng phiếu',
        minOrderNotMet: 'Chưa đạt số tiền tối thiểu',
        expired: 'Đã hết hạn',
        notUsable: 'Phiếu không khả dụng',
      },
      preview: {
        title: 'Số tiền thanh toán',
        orderTotal: 'Tổng đơn hàng',
        tip: 'Tiền boa',
        points: 'Điểm',
        coupon: 'Phiếu giảm giá',
        chargeAmount: 'Số tiền thu',
      },
      zeroChargeConfirm: 'Xác nhận thanh toán toàn bộ',
    },
    errors: {
      notSettleable: 'Đơn hàng này hiện không thể thanh toán.',
      processingCannotCancel: 'Thanh toán đang được xử lý. Vui lòng thử lại sau giây lát.',
      untrustedChallenge: 'Không thể mở trang xác thực thanh toán.',
      adjustmentValidationFailed: 'Không thể áp dụng giảm giá hoặc điểm. Vui lòng kiểm tra lại.',
      cardRequiredForCharge: 'Vẫn còn số tiền phải thanh toán, cần có thẻ.',
      generic: 'Thanh toán thất bại. Vui lòng thử lại.',
    },
  },
  th: {
    button: 'ชำระเงินตอนนี้',
    modalTitle: 'ชำระเงินตอนนี้',
    modalDescription: 'ชำระเงินคำสั่งซื้อที่ค้างชำระด้วยบัตร',
    processing: 'กำลังประมวลผลการชำระเงิน...',
    session: {
      bannerSummary: 'มี {count} ออร์เดอร์ที่ยังไม่ชำระในเซสชันนี้ · {amount}',
      payAllButton: 'ชำระทั้งหมด',
      modalTitle: 'ชำระทั้งหมด',
      modalDescription: 'ชำระ {count} ออร์เดอร์ที่ค้างอยู่ในครั้งเดียว',
      recoveryNotice: 'ไม่พบข้อมูลเซสชัน จึงไม่สามารถชำระเงินได้ กรุณาสแกน QR โค้ดของโต๊ะอีกครั้ง',
      progressLabel: 'กำลังดำเนินการ {count} รายการ',
      payButton: 'ชำระเงิน',
      sheetTitle: 'ออร์เดอร์ของโต๊ะเรา',
      sheetTotalLabel: 'รวม',
      sheetPayButton: 'ชำระทั้งหมด',
      sheetEmpty: 'ไม่มีออร์เดอร์ที่จะแสดง',
      sheetClose: 'ปิด',
      chargeBack: 'ย้อนกลับ',
      orderSubtotal: 'ยอดรวมย่อย',
    },
    adjustments: {
      sectionTitle: 'การชำระเงินเพิ่มเติม',
      tip: {
        label: 'เลือกทิปสำหรับพนักงาน',
        customLabel: 'อื่นๆ',
        amountLabel: 'ทิปโดยประมาณ',
        enterTip: 'ป้อนทิป',
        percentage: '%',
        amount: 'จำนวน',
        enterTipPercentage: 'ป้อนเปอร์เซ็นต์ทิป',
        enterTipAmount: 'ป้อนจำนวนทิป',
        estimatedTip: 'ทิปโดยประมาณ',
        cancel: 'ยกเลิก',
        apply: 'ใช้',
      },
      points: {
        sectionTitle: 'ใช้คะแนน',
        available: 'คะแนนที่ใช้ได้',
        usable: 'ใช้ได้',
        placeholder: 'คะแนนที่จะใช้',
        apply: 'ใช้',
        useAll: 'ใช้ทั้งหมด',
        discountApplied: 'ใช้ส่วนลดแล้ว',
        minAmount: 'จำนวนขั้นต่ำ:',
        loginPrompt: 'เข้าสู่ระบบเพื่อใช้คะแนน',
      },
      coupon: {
        title: 'คูปอง',
        none: 'ไม่ใช้คูปอง',
        minOrderNotMet: 'ยอดสั่งซื้อไม่ถึงขั้นต่ำ',
        expired: 'หมดอายุ',
        notUsable: 'คูปองไม่พร้อมใช้งาน',
      },
      preview: {
        title: 'จำนวนเงินที่ต้องชำระ',
        orderTotal: 'ยอดคำสั่งซื้อ',
        tip: 'ทิป',
        points: 'คะแนน',
        coupon: 'คูปอง',
        chargeAmount: 'จำนวนเงินที่เรียกเก็บ',
      },
      zeroChargeConfirm: 'ยืนยันการชำระเงินเต็มจำนวน',
    },
    errors: {
      notSettleable: 'ไม่สามารถชำระเงินคำสั่งซื้อนี้ได้ในขณะนี้',
      processingCannotCancel: 'กำลังประมวลผลการชำระเงิน โปรดลองอีกครั้งในอีกสักครู่',
      untrustedChallenge: 'ไม่สามารถเปิดหน้ายืนยันการชำระเงินได้',
      adjustmentValidationFailed: 'ไม่สามารถใช้ส่วนลดหรือคะแนนได้ โปรดตรวจสอบอีกครั้ง',
      cardRequiredForCharge: 'ยังมียอดที่ต้องชำระ จำเป็นต้องใช้บัตร',
      generic: 'การชำระเงินล้มเหลว โปรดลองอีกครั้ง',
    },
  },
  nl: {
    button: 'Nu betalen',
    modalTitle: 'Nu betalen',
    modalDescription: 'Betaal uw openstaande bestelling met kaart.',
    processing: 'Betaling wordt verwerkt...',
    session: {
      bannerSummary: '{count} onbetaalde bestellingen in deze sessie · {amount}',
      payAllButton: 'Alles betalen',
      modalTitle: 'Alles betalen',
      modalDescription: 'Betaal {count} openstaande bestellingen in één keer.',
      recoveryNotice: 'We konden je sessie niet vinden, betaling is niet mogelijk. Scan de QR-code op de tafel opnieuw.',
      progressLabel: '{count} in behandeling',
      payButton: 'Betalen',
      sheetTitle: 'Bestelling van onze tafel',
      sheetTotalLabel: 'Totaal',
      sheetPayButton: 'Alles betalen',
      sheetEmpty: 'Geen bestellingen om te tonen.',
      sheetClose: 'Sluiten',
      chargeBack: 'Terug',
      orderSubtotal: 'Subtotaal',
    },
    adjustments: {
      sectionTitle: 'Aanvullende betaling',
      tip: {
        label: 'Selecteer fooi voor de bediening',
        customLabel: 'ANDERE',
        amountLabel: 'Geschatte fooi',
        enterTip: 'Fooi invoeren',
        percentage: '%',
        amount: 'Bedrag',
        enterTipPercentage: 'Voer fooipercentage in',
        enterTipAmount: 'Voer fooibedrag in',
        estimatedTip: 'Geschatte fooi',
        cancel: 'Annuleren',
        apply: 'Toepassen',
      },
      points: {
        sectionTitle: 'Punten gebruiken',
        available: 'Beschikbare punten',
        usable: 'Bruikbaar',
        placeholder: 'Te gebruiken punten',
        apply: 'Toepassen',
        useAll: 'Alles gebruiken',
        discountApplied: 'korting toegepast',
        minAmount: 'Minimumbedrag:',
        loginPrompt: 'Log in om punten te gebruiken',
      },
      coupon: {
        title: 'Coupon',
        none: 'Geen coupon',
        minOrderNotMet: 'Minimumbedrag niet bereikt',
        expired: 'Verlopen',
        notUsable: 'Coupon niet beschikbaar',
      },
      preview: {
        title: 'Te betalen bedrag',
        orderTotal: 'Besteltotaal',
        tip: 'Fooi',
        points: 'Punten',
        coupon: 'Coupon',
        chargeAmount: 'Af te schrijven bedrag',
      },
      zeroChargeConfirm: 'Volledige betaling bevestigen',
    },
    errors: {
      notSettleable: 'Deze bestelling kan op dit moment niet worden betaald.',
      processingCannotCancel: 'De betaling wordt verwerkt. Probeer het over een paar momenten opnieuw.',
      untrustedChallenge: 'De pagina voor betalingsverificatie kon niet worden geopend.',
      adjustmentValidationFailed: 'De kortingen of punten konden niet worden toegepast. Controleer ze opnieuw.',
      cardRequiredForCharge: 'Er resteert een te betalen bedrag, een kaart is vereist.',
      generic: 'De betaling is mislukt. Probeer het opnieuw.',
    },
  },
};
